import { useState, useEffect, useRef, useMemo } from "react";
import usePersistentPageState from "../hooks/usePersistentPageState";
import { useNavigate } from "react-router-dom";
import {
  FaSlidersH,
  FaSyncAlt,
  FaUniversity,
  FaCalendarAlt,
  FaEnvelope,
} from "react-icons/fa";
import Layout from "./Layout";
import usePageMeta from "../hooks/usePageMeta";
import { useAppPermissions } from "../context/PermissionContext";
import { BASE_URL } from "../config";
import ActionDropdown from "../components/ActionDropdown";
import { showError, showSuccess, showConfirm } from "../utils/dialog";
import {
  formatAppDate,
  formatAppIntlLabel,
  formatAppTime,
  parseDbDateTime,
} from "../utils/appDateTime";
import AddPayerModal from "../components/forms/AddPayerModal";
import AddAdjustmentMoal from "../components/forms/AddAdjustmentMoal";
import EditAdjustmentModal from "../components/forms/EditAdjustmentModal";
import EditPayerModal from "../components/forms/EditPayerModal";
import ClientDetailModal from "../components/ClientDetailModal";
import PersonnelDetailModal from "../components/PersonnelDetailModal";
import RichAuditsPanel from "../components/RichAuditsPanel";
import StaffAvatarCircle from "../components/StaffAvatarCircle";
import {
  accountAvatarImgSrc,
  resolveBackendUrl,
} from "../utils/accountAvatarUrl";

import {
  AtlasFilterPanel,
  AtlasFilterChipBar,
  useFilterPanel,
  AccountingWeeksFilterFields,
  buildAccountingWeeksFilterChips,
  AccountingPayStubsFilterFields,
  buildAccountingPayStubsFilterChips,
  AccountingPayrollSummaryFilterFields,
  buildAccountingPayrollSummaryFilterChips,
  AccountingClientReportsFilterFields,
  buildAccountingClientReportsFilterChips,
  AccountingAdjustmentFilterFields,
  buildAccountingAdjustmentFilterChips,
  AccountingPayersFilterFields,
  buildAccountingPayersFilterChips,
} from "../components/filters";
import { AtlasListToolbar } from "../components/list-toolbar";
import {
  AtlasPageHeader,
  AtlasPageDivider,
  AtlasTabBar,
} from "../components/page-layout";
import {
  ATLAS_BTN_PRIMARY as ATLAS_TOOLBAR_BTN_PRIMARY,
  ATLAS_BTN_SECONDARY as ATLAS_TOOLBAR_BTN_SECONDARY,
} from "../components/atlasButtonStyles";
import {
  formatDateChipLabel,
  formatPayPeriodLabel,
  formatWeekChipLabel,
  parseAccountingLocalDate,
} from "../components/filters/config/accountingFilterUtils";

const ACCOUNTING_TAB_IDS = [
  "WEEKS",
  "PAY STUBS",
  "PAYROLL SUMMARY",
  "CLIENT REPORTS",
  "ADJUSTMENT",
  "PAYERS",
];

const accountingTabUsesWeekFilter = (tab) =>
  tab === "WEEKS" || tab === "PAY STUBS" || tab === "PAYROLL SUMMARY";

const readInitialAccountingSelectedTab = () => {
  try {
    const saved = sessionStorage.getItem("accounting_selected_tab");
    return ACCOUNTING_TAB_IDS.includes(saved) ? saved : "WEEKS";
  } catch (e) {
    return "WEEKS";
  }
};

/** Local calendar date for date inputs (not UTC). */
const accountingLocalYyyyMmDd = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const CLIENT_REPORT_APP_NAME = "Talent";

const formatLegacyClientReportDate = (dateStr) => {
  const iso = String(dateStr || "").slice(0, 10);
  const anchor = parseDbDateTime(`${iso}T12:00:00Z`);
  if (!anchor) return "";
  const month = formatAppIntlLabel(anchor, { month: "short" });
  const day = formatAppIntlLabel(anchor, { day: "numeric" });
  const year = formatAppIntlLabel(anchor, { year: "numeric" });
  return `${month} ${day} ${year}`;
};

const buildClientReportPdfFilename = (clientName, weekStartDate) => {
  const safeName = String(clientName || "Client Report")
    .replace(/[\\/]/g, " ")
    .replace(/[<>:"|?*]/g, "")
    .trim();
  const dateLabel = formatLegacyClientReportDate(weekStartDate);
  return `${safeName} - ${CLIENT_REPORT_APP_NAME} - ${dateLabel}.pdf`;
};

const buildClientReportsZipFilename = (weekStartDate) => {
  const dateLabel = formatLegacyClientReportDate(weekStartDate);
  return `Client Reports - ${CLIENT_REPORT_APP_NAME} - ${dateLabel}.zip`;
};

const ACCOUNTING_STAT_LOCALE = "en-US";

/** Stat cards: whole numbers with grouping (e.g. 1,234). */
const formatAccountingStatInt = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0";
  return Math.trunc(x).toLocaleString(ACCOUNTING_STAT_LOCALE);
};

/** Stat cards: currency with grouping (e.g. $82,658.70). */
const formatAccountingStatMoney = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "$0.00";
  return `$${x.toLocaleString(ACCOUNTING_STAT_LOCALE, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const accountingLineTravelHours = (line) => {
  const n = Number(line?.travel_hours ?? line?.travel_time_amount ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const accountingLineTravelAmount = (line) => {
  const explicit = Number(line?.travel_amount ?? line?.travel_pay ?? 0);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;
  const hours = accountingLineTravelHours(line);
  const rate = Number(line?.rate ?? 0);
  if (hours > 0 && Number.isFinite(rate) && rate > 0) {
    return hours * rate;
  }
  return 0;
};

const accountingLineShiftTotal = (line) => {
  const total = Number(line?.total);
  const travelAmount = accountingLineTravelAmount(line);
  if (Number.isFinite(total)) {
    return Math.max(0, total - travelAmount);
  }
  const hours = Number(line?.hours ?? 0);
  const rate = Number(line?.rate ?? 0);
  return Number.isFinite(hours) && Number.isFinite(rate) ? hours * rate : 0;
};

const accountingLineTravelRate = (line) => {
  const travelType = String(line?.travel_time_type || "").trim().toLowerCase();
  if (travelType === "flat_rate") return accountingLineTravelAmount(line);
  const rate = Number(line?.rate ?? 0);
  return Number.isFinite(rate) ? rate : 0;
};

/** Stat cards: decimals with grouping (e.g. 3,912.42 or 10.65%). */
const formatAccountingStatDecimal = (n, fractionDigits = 2) => {
  const x = Number(n);
  if (!Number.isFinite(x)) {
    return (0).toLocaleString(ACCOUNTING_STAT_LOCALE, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  }
  return x.toLocaleString(ACCOUNTING_STAT_LOCALE, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

const Accounting = () => {
  usePageMeta("Accounting");
  const filterPanel = useFilterPanel();
  const navigate = useNavigate();
  const { canWriteAccounting } = useAppPermissions();
  const accountingReadOnly = !canWriteAccounting;

  const tabs = ACCOUNTING_TAB_IDS;
  const [selectedTab, setSelectedTab] = useState("WEEKS");
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loadingRefresh, setLoadingRefresh] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [innerTab, setInnerTab] = useState("Client Summary");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showAudit, setShowAudit] = useState(false);
  const [audits, setAudits] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [showAuditFilterPanel, setShowAuditFilterPanel] = useState(false);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditOffset, setAuditOffset] = useState(0);
  const [auditLimit, setAuditLimit] = useState(25);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditSubjectType, setAuditSubjectType] = useState("");
  const [auditDetailType, setAuditDetailType] = useState("");
  const [auditActionType, setAuditActionType] = useState("");
  const [auditShowObjectId, setAuditShowObjectId] = useState(true);
  const [auditSortBy, setAuditSortBy] = useState("Created");
  const [auditSortDirection, setAuditSortDirection] = useState("Descending");
  const [auditRefreshTick, setAuditRefreshTick] = useState(0);
  const [showAuditLimitDropdown, setShowAuditLimitDropdown] = useState(false);
  const [auditActiveFilterField, setAuditActiveFilterField] = useState("");
  const [openModal, setOpenModal] = useState(null);
  const [showPaystubModal, setShowPaystubModal] = useState(false);
  const [activePaystub, setActivePaystub] = useState(null);
  const [showClientRowModal, setShowClientRowModal] = useState(false);
  const [activeClientRow, setActiveClientRow] = useState(null);
  const [showFieldRowModal, setShowFieldRowModal] = useState(false);
  const [activeFieldRow, setActiveFieldRow] = useState(null);
  // Client detail modal state
  const [detailClient, setDetailClient] = useState(null);
  const [showClientDetailModal, setShowClientDetailModal] = useState(false);
  // Personnel detail modal state (Payroll Summary -> Temp Staff)
  const [detailPerson, setDetailPerson] = useState(null);
  const [showPersonnelDetailModal, setShowPersonnelDetailModal] =
    useState(false);

  // Accounting state (WEEKS tab)
  const [week, setWeek] = useState(null);
  const [stats, setStats] = useState(null);
  const [clientSummary, setClientSummary] = useState([]);
  const [fieldstaffSummary, setFieldstaffSummary] = useState([]);
  // Other tabs
  const [paystubs, setPaystubs] = useState([]);
  const [paystubStats, setPaystubStats] = useState({});
  const [payroll, setPayroll] = useState([]);
  const [clients, setClients] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [editAdjustment, setEditAdjustment] = useState(null);
  const [showEditAdjustmentModal, setShowEditAdjustmentModal] = useState(false);
  const [editPayer, setEditPayer] = useState(null);
  const [showEditPayerModal, setShowEditPayerModal] = useState(false);
  const [payers, setPayers] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);

  // Filter states for each tab
  const [filterClient, setFilterClient] = useState("");
  const [filterClientId, setFilterClientId] = useState(null);
  const [filterClientIds, setFilterClientIds] = useState([]);
  const [filterStaff, setFilterStaff] = useState("");
  const [filterStaffId, setFilterStaffId] = useState(null);
  const [filterStaffIds, setFilterStaffIds] = useState([]);
  const [filterPayer, setFilterPayer] = useState("");
  const [filterWeek, setFilterWeek] = useState("");
  const [filterDate, setFilterDate] = useState(() =>
    readInitialAccountingSelectedTab() === "CLIENT REPORTS"
      ? accountingLocalYyyyMmDd()
      : "",
  );
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [filterSubject, setFilterSubject] = useState("");
  const [filterShowDeleted, setFilterShowDeleted] = useState(false);

  const searchInputRef = useRef(null);
  const auditSubjectTypeRef = useRef(null);
  const auditDetailTypeRef = useRef(null);
  const auditActionTypeRef = useRef(null);
  const auditShowObjectIdRef = useRef(null);
  const auditSortByRef = useRef(null);
  const auditSortDirectionRef = useRef(null);

  // Sorting state (simple, per tab default)
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");

  // Autocomplete suggestion state
  const [weekFilterClients, setWeekFilterClients] = useState([]);
  const [weekFilterStaff, setWeekFilterStaff] = useState([]);
  const [payerList, setPayerList] = useState([]);
  const [filterPayerId, setFilterPayerId] = useState(null);
  const [accountPayers, setAccountPayers] = useState({});
  const [accountPaymentMethods, setAccountPaymentMethods] = useState({});
  const [selectedStaffLookup, setSelectedStaffLookup] = useState({});
  const [selectedPayerLookup, setSelectedPayerLookup] = useState({});
  const buildDefaultTabFilters = (tab = "") => ({
    searchTerm: "",
    filterClient: "",
    filterClientId: null,
    filterClientIds: [],
    filterStaff: "",
    filterStaffId: null,
    filterStaffIds: [],
    filterPayer: "",
    filterPayerId: null,
    filterWeek: "",
    filterDate: "",
    filterFromDate: "",
    filterToDate: "",
    filterSubject: "",
    filterShowDeleted: false,
    sortKey:
      tab === "ADJUSTMENT"
        ? "adj_id"
        : tab === "CLIENT REPORTS"
          ? "parent_name"
          : "name",
    sortDir: "asc",
  });
  const [tabScopedFilters, setTabScopedFilters] = usePersistentPageState(
    "page_filters_accounting",
    () => {
      const scoped = {};
      const today = accountingLocalYyyyMmDd();
      tabs.forEach((tab) => {
        scoped[tab] = buildDefaultTabFilters(tab);
        if (tab === "CLIENT REPORTS") {
          scoped[tab] = { ...scoped[tab], filterDate: today };
        }
      });
      return scoped;
    }
  );
  const isRestoringTabFiltersRef = useRef(false);
  const seededDefaultWeekFilterRef = useRef(false);

  const captureCurrentFilters = () => ({
    searchTerm,
    filterClient,
    filterClientId,
    filterClientIds: Array.isArray(filterClientIds) ? [...filterClientIds] : [],
    filterStaff,
    filterStaffId,
    filterStaffIds: Array.isArray(filterStaffIds) ? [...filterStaffIds] : [],
    filterPayer,
    filterPayerId,
    filterWeek,
    filterDate,
    filterFromDate,
    filterToDate,
    filterSubject,
    filterShowDeleted,
    sortKey,
    sortDir,
  });

  const applyTabFilters = (filters) => {
    const next = filters || buildDefaultTabFilters(selectedTab);
    setSearchTerm(next.searchTerm || "");
    setFilterClient(next.filterClient || "");
    setFilterClientId(next.filterClientId ?? null);
    setFilterClientIds(
      Array.isArray(next.filterClientIds) ? [...next.filterClientIds] : [],
    );
    setFilterStaff(next.filterStaff || "");
    setFilterStaffId(next.filterStaffId ?? null);
    setFilterStaffIds(
      Array.isArray(next.filterStaffIds) ? [...next.filterStaffIds] : [],
    );
    setFilterPayer(next.filterPayer || "");
    setFilterPayerId(next.filterPayerId ?? null);
    setFilterWeek(next.filterWeek || "");
    setFilterDate(next.filterDate || "");
    setFilterFromDate(next.filterFromDate || "");
    setFilterToDate(next.filterToDate || "");
    setFilterSubject(next.filterSubject || "");
    setFilterShowDeleted(!!next.filterShowDeleted);
    setSortKey(next.sortKey || "name");
    setSortDir(next.sortDir || "asc");
  };

  const handleTabChange = (nextTab) => {
    if (nextTab === selectedTab) return;

    setTabScopedFilters((prev) => ({
      ...prev,
      [selectedTab]: captureCurrentFilters(),
    }));

    const base = tabScopedFilters[nextTab] || buildDefaultTabFilters(nextTab);
    let nextFilters =
      nextTab === "CLIENT REPORTS" && !String(base.filterDate || "").trim()
        ? { ...base, filterDate: accountingLocalYyyyMmDd() }
        : { ...base };
    if (
      accountingTabUsesWeekFilter(nextTab) &&
      !String(nextFilters.filterWeek || "").trim() &&
      week?.start_date
    ) {
      nextFilters = { ...nextFilters, filterWeek: week.start_date };
    }
    isRestoringTabFiltersRef.current = true;
    applyTabFilters(nextFilters);
    setSelectedTab(nextTab);
    setCurrentPage(1);
    setTimeout(() => {
      isRestoringTabFiltersRef.current = false;
    }, 0);
  };

  useEffect(() => {
    if (isRestoringTabFiltersRef.current) return;
    setTabScopedFilters((prev) => ({
      ...prev,
      [selectedTab]: captureCurrentFilters(),
    }));
  }, [
    selectedTab,
    searchTerm,
    filterClient,
    filterClientId,
    filterClientIds,
    filterStaff,
    filterStaffId,
    filterStaffIds,
    filterPayer,
    filterPayerId,
    filterWeek,
    filterDate,
    filterFromDate,
    filterToDate,
    filterSubject,
    filterShowDeleted,
    sortKey,
    sortDir,
  ]);

  // Apply tab filters on initial mount if we have restored them
  useEffect(() => {
    const nextFilters = tabScopedFilters[selectedTab] || buildDefaultTabFilters(selectedTab);
    isRestoringTabFiltersRef.current = true;
    applyTabFilters(nextFilters);
    setTimeout(() => {
      isRestoringTabFiltersRef.current = false;
    }, 0);
  }, []); // Run only once on mount





  const getStaffDisplayName = (staff) => {
    if (!staff) return "";
    const direct =
      staff.name ||
      staff.full_name ||
      staff.fullName ||
      staff.display_name ||
      staff.displayName ||
      "";
    if (direct) return String(direct).trim();

    const first =
      staff.first_name || staff.firstName || staff.given_name || staff.givenName || "";
    const last =
      staff.last_name || staff.lastName || staff.surname || staff.familyName || "";
    const combined = `${first || ""} ${last || ""}`.trim();
    return combined;
  };

  const fetchJson = async (url, opts) => {
    try {
      const res = await fetch(url, opts);
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`${res.status} ${res.statusText} - ${t}`);
      }
      return await res.json();
    } catch (e) {
      // Ignore aborts from effect cleanups
      if (e.name === "AbortError") {
        return null;
      }
      console.error("Accounting fetch error", url, e);
      setErrorMsg(`Failed: ${url} → ${e.message}`);
      return null;
    }
  };

  const openEditPayer = async (payer) => {
    try {
      const json = await fetchJson(
        `${BASE_URL}/api/accounting/payers/${payer.payer_id}`,
      );
      setEditPayer(json?.data || payer);
    } catch (e) {
      setEditPayer(payer);
    } finally {
      setShowEditPayerModal(true);
    }
  };

  // Load current week once so we can pass weekId to all calls
  useEffect(() => {
    const controller = new AbortController();
    const loadWeek = async () => {
      try {
        const url = filterWeek
          ? `${BASE_URL}/api/accounting/weeks?startDate=${encodeURIComponent(filterWeek)}`
          : `${BASE_URL}/api/accounting/weeks`;
        const wJson = await fetchJson(url, { signal: controller.signal });
        const data = wJson?.data || null;
        setWeek(data);
        if (
          !seededDefaultWeekFilterRef.current &&
          data?.start_date &&
          accountingTabUsesWeekFilter(selectedTab) &&
          !filterWeek
        ) {
          seededDefaultWeekFilterRef.current = true;
          setFilterWeek(data.start_date);
          setSelectedDate((prev) => prev || data.start_date);
          setSelectedYear(
            (prev) => prev || new Date(`${data.start_date}T12:00:00`).getFullYear(),
          );
        }
      } catch (e) {
        console.error("Load week failed", e);
        setWeek(null);
      }
    };
    if (!week) loadWeek();
    return () => controller.abort();
  }, []);

  // Keep week filter chip in sync when default week loads (WEEKS / PAY STUBS / PAYROLL SUMMARY).
  useEffect(() => {
    if (seededDefaultWeekFilterRef.current) return;
    if (!week?.start_date || filterWeek) return;
    if (!accountingTabUsesWeekFilter(selectedTab)) return;
    seededDefaultWeekFilterRef.current = true;
    setFilterWeek(week.start_date);
    setSelectedDate((prev) => prev || week.start_date);
  }, [week?.start_date, filterWeek, selectedTab]);

  const fetchWeekByStart = async (startIso) => {
    try {
      setWeek(null);
      setStats(null);
      setClientSummary([]);
      setFieldstaffSummary([]);
      // Avoid stale PAY STUBS / PAYROLL SUMMARY stats while the new week loads.
      setPaystubs([]);
      setPaystubStats({});
      setPayroll({});
      const wJson = await fetchJson(
        `${BASE_URL}/api/accounting/weeks?startDate=${startIso}`,
      );
      setWeek(wJson?.data || null);
    } catch (e) {
      console.error("Fetch week by start failed", e);
    }
  };

  useEffect(() => {
    if (
      selectedTab !== "WEEKS" &&
      selectedTab !== "PAY STUBS" &&
      selectedTab !== "PAYROLL SUMMARY" &&
      selectedTab !== "CLIENT REPORTS"
    ) {
      return;
    }
    const activeDate =
      selectedTab === "CLIENT REPORTS" ? filterDate : filterWeek;
    if (!activeDate) return;
    fetchWeekByStart(activeDate);
  }, [filterWeek, filterDate, selectedTab]);

  useEffect(() => {
    if (!accountingTabUsesWeekFilter(selectedTab)) return;
    if (!String(filterWeek || "").trim()) return;
    if (String(selectedDate || "") === String(filterWeek || "")) return;
    setSelectedDate(filterWeek);
  }, [filterWeek, selectedDate, selectedTab]);

  // CLIENT REPORTS date picker defaults to today when empty (e.g. session restore).
  useEffect(() => {
    if (selectedTab !== "CLIENT REPORTS") return;
    if (isRestoringTabFiltersRef.current) return;
    if (String(filterDate || "").trim()) return;
    setFilterDate(accountingLocalYyyyMmDd());
  }, [selectedTab, filterDate]);

  const onChangeWeekDate = async (e) => {
    const val = e.target.value;
    setSelectedDate(val);
    if (val) await fetchWeekByStart(val);
  };

  const onChangeYear = async (e) => {
    const yr = parseInt(e.target.value, 10);
    setSelectedYear(yr);
    // Pick first Monday of the selected year as a reasonable default
    const d = new Date(Date.UTC(yr, 0, 1));
    const day = d.getUTCDay();
    const diffToMonday = (day + 6) % 7;
    d.setUTCDate(d.getUTCDate() - diffToMonday);
    const iso = d.toISOString().slice(0, 10);
    setSelectedDate(iso);
    await fetchWeekByStart(iso);
  };

  // Load week + stats + summaries for WEEKS
  useEffect(() => {
    if (selectedTab !== "WEEKS") return;
    const controller = new AbortController();
    let isMounted = true;
    const load = async () => {
      setLoadingRefresh(true);
      try {
        // ensure we have a week first
        let currentWeek = week;
        if (!currentWeek) {
          const url = filterWeek
            ? `${BASE_URL}/api/accounting/weeks?startDate=${encodeURIComponent(filterWeek)}`
            : `${BASE_URL}/api/accounting/weeks`;
          const wJson = await fetchJson(url, { signal: controller.signal });
          currentWeek = wJson?.data || null;
          if (isMounted) setWeek(currentWeek);
          // Don't continue in this run - let the next effect run with the week set
          if (isMounted) setLoadingRefresh(false);
          return;
        }
        const paramKey = currentWeek?.week_id ? "weekId" : "startDate";
        const paramVal = currentWeek?.week_id || currentWeek?.start_date || "";
        const weekParams = new URLSearchParams();

if (currentWeek?.start_date) {
  weekParams.set("startDate", currentWeek.start_date);
}

if (currentWeek?.end_date) {
  weekParams.set("endDate", currentWeek.end_date);
}

        // weekParams.set(paramKey, paramVal);
        if (searchTerm?.trim()) weekParams.set("search", searchTerm.trim());
        if (Array.isArray(filterStaffIds) && filterStaffIds.length > 0) {
          weekParams.set("staffIds", filterStaffIds.join(","));
        } else if (filterStaffId) {
          weekParams.set("staffId", String(filterStaffId));
        }
        if (filterPayerId) weekParams.set("payerId", String(filterPayerId));
        if (Array.isArray(filterClientIds) && filterClientIds.length > 0) {
          weekParams.set("clientIds", filterClientIds.join(","));
        } else if (filterClientId) {
          weekParams.set("clientId", String(filterClientId));
        }
        const sJson = await fetchJson(
          `${BASE_URL}/api/accounting/shifts/stats?${weekParams.toString()}`,
          { signal: controller.signal },
        );
        const clientJson = await fetchJson(
          `${BASE_URL}/api/accounting/reports/pay?groupby=client&weeksMode=legacy&${weekParams.toString()}`,
          { signal: controller.signal },
        );
        const payrollJson = await fetchJson(
          `${BASE_URL}/api/accounting/reports/pay?groupby=fieldstaff&weeksMode=legacy&${weekParams.toString()}`,
          { signal: controller.signal },
        );
        if (isMounted) {
          const payrollRows = Array.isArray(payrollJson?.data)
            ? payrollJson.data
            : [];
          const payrollStaffTotal =
            Number(payrollJson?.total || 0) || payrollRows.length || 0;
          setPayroll(
            Array.isArray(payrollRows)
              ? {
                  payroll: payrollRows,
                  staff: payrollStaffTotal,
                  totalPayBeforeTax: payrollRows.reduce(
                    (sum, row) => sum + Number(row?.pay || 0),
                    0,
                  ),
                }
              : {},
          );
          setStats(sJson || null);
          setClientSummary(
            Array.isArray(clientJson?.data) ? clientJson.data : [],
          );
          setFieldstaffSummary(payrollRows);
          // set totals for WEEKS view based on innerTab (client or staff summary)
          try {
            const clientTotal =
              clientJson?.total ||
              (Array.isArray(clientJson?.data) ? clientJson.data.length : 0);
            const staffTotal = payrollStaffTotal;
            const weekTotal =
              innerTab === "Client Summary" ? clientTotal : staffTotal;
            setTotalResults(weekTotal);
            setTotalPages(Math.max(1, Math.ceil(weekTotal / rowsPerPage)));
          } catch (e) {
            setTotalResults(0);
            setTotalPages(1);
          }
        }
      } catch (e) {
        console.error("Accounting weeks load error", e);
        if (isMounted) {
          setWeek(null);
          setStats(null);
          setClientSummary([]);
          setFieldstaffSummary([]);
        }
      } finally {
        if (isMounted) setLoadingRefresh(false);
      }
    };
    load();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [
    selectedTab,
    week,
    innerTab,
    refreshTick,
    searchTerm,
    filterWeek,
    filterStaffId,
    filterStaffIds,
    filterPayerId,
    filterClientId,
    filterClientIds,
  ]);

  // Load per-tab data (pagination shared)
  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoadingRefresh(true);
      try {
        if (selectedTab === "PAYROLL SUMMARY") {
          const paramKey = week?.week_id ? "weekId" : "startDate";
          const paramVal = week?.week_id || week?.start_date || "";
          const payerParam = filterPayerId ? `&payerId=${filterPayerId}` : "";
          const staffParam =
            Array.isArray(filterStaffIds) && filterStaffIds.length > 0
              ? `&staffIds=${encodeURIComponent(filterStaffIds.join(","))}`
              : filterStaffId
                ? `&staffId=${encodeURIComponent(filterStaffId)}`
                : "";
          const json = await fetchJson(
            `${BASE_URL}/api/accounting/reports/payroll?${paramKey}=${paramVal}${payerParam}${staffParam}`,
            { signal: controller.signal },
          );
          // Prevent stale staff totals from the previous week/date.
          setPayroll({});
          setPayroll(json?.data || {});
          const payrollRows = Array.isArray(json?.data?.payroll)
            ? json.data.payroll
            : [];
          setFieldstaffSummary(payrollRows);
          const staffTotal =
            Number(json?.data?.staff || 0) || payrollRows.length || 0;
          setTotalResults(staffTotal);
          setTotalPages(Math.max(1, Math.ceil(staffTotal / rowsPerPage)));
        } else if (selectedTab === "CLIENT REPORTS") {
          const selectedIds = [
            ...(Array.isArray(filterClientIds) ? filterClientIds : []),
            filterClientId,
          ]
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0);
          const searchVal = searchTerm;
          const weekParams = new URLSearchParams();
          weekParams.set("groupby", "client");
          weekParams.set("weeksMode", "legacy");
          if (week?.week_id) {
            weekParams.set("weekId", String(week.week_id));
          } else if (week?.start_date || filterDate) {
            weekParams.set(
              "startDate",
              String(week?.start_date || filterDate),
            );
          }
          if (searchVal) {
            weekParams.set("search", searchVal);
          }
          if (selectedIds.length > 0) {
            weekParams.set("clientIds", selectedIds.join(","));
          }
          const json = await fetchJson(
            `${BASE_URL}/api/accounting/reports/pay?${weekParams.toString()}`,
            { signal: controller.signal },
          );
          const rows = Array.isArray(json?.data) ? json.data : [];
          setClients(
            rows.map((r) => ({
              client_id: r.client_id || r.clientId,
              name: r.name || "",
              avatar_url: r.avatar_url || "",
              parent_client:
                r.parent_client ||
                (r.parent_client_name ? { name: r.parent_client_name } : null),
            })),
          );
          const total = Number(json?.total || 0) || rows.length;
          setTotalResults(total);
          setTotalPages(Math.max(1, Math.ceil(total / rowsPerPage)));
        } else if (selectedTab === "ADJUSTMENT") {
          const showDeletedParam = filterShowDeleted ? "&showDeleted=1" : "";
          const fromParam = filterFromDate
            ? `&fromDate=${encodeURIComponent(filterFromDate)}`
            : "";
          const toParam = filterToDate
            ? `&toDate=${encodeURIComponent(filterToDate)}`
            : "";
          const url = `${BASE_URL}/api/accounting/adjustments?page=${currentPage}&limit=${rowsPerPage}&search=${encodeURIComponent(searchTerm)}${showDeletedParam}${fromParam}${toParam}`;
          const json = await fetchJson(url, { signal: controller.signal });
          setAdjustments(Array.isArray(json?.data) ? json.data : []);
          const adjustmentTotal = Number(json?.total ?? 0);
          const adjustmentTotalPages = Number(json?.totalPages ?? 0);
          setTotalPages(
            adjustmentTotalPages > 0
              ? adjustmentTotalPages
              : Math.max(1, Math.ceil(adjustmentTotal / rowsPerPage)),
          );
          setTotalResults(adjustmentTotal);
        } else if (selectedTab === "PAYERS") {
          const statusParam = filterShowDeleted ? "all" : "active";
          let json = await fetchJson(
            `${BASE_URL}/api/accounting/payers?status=${statusParam}`,
            { signal: controller.signal },
          );
          // Backward compatibility: older backend may not support status=deleted.
          if (!json && filterShowDeleted) {
            json = await fetchJson(
              `${BASE_URL}/api/accounting/payers?status=all`,
              { signal: controller.signal },
            );
          }
          setPayers(Array.isArray(json?.data) ? json.data : []);
          setTotalResults(
            json?.total || (Array.isArray(json?.data) ? json.data.length : 0),
          );
          setTotalPages(
            Math.max(
              1,
              Math.ceil(
                (json?.total ||
                  (Array.isArray(json?.data) ? json.data.length : 0)) /
                  rowsPerPage,
              ),
            ),
          );
        }
      } catch (e) {
        console.error("Accounting tab load error", e);
        if (selectedTab === "PAYROLL SUMMARY") {
          setPayroll({});
        }
        if (selectedTab === "CLIENT REPORTS") {
          setClients([]);
        }
        if (selectedTab === "ADJUSTMENT") {
          setAdjustments([]);
        }
        if (selectedTab === "PAYERS") {
          setPayers([]);
        }
      } finally {
        setLoadingRefresh(false);
      }
    };
    load();
    return () => controller.abort();
  }, [
    selectedTab,
    currentPage,
    rowsPerPage,
    searchTerm,
    week,
    filterPayerId,
    filterWeek,
    filterStaffId,
    filterStaffIds,
    filterClient,
    filterClientId,
    filterClientIds,
    filterDate,
    filterFromDate,
    filterToDate,
    filterSubject,
    filterShowDeleted,
    refreshTick,
  ]);

  // Always keep paystubs fresh so an adjustment created from another tab
  // (e.g. ADJUSTMENT) is reflected the next time the user lands on PAY STUBS.
  useEffect(() => {
    if (!week) return undefined;
    const controller = new AbortController();
    const paramKey = week.week_id ? "weekId" : "startDate";
    const paramVal = week.week_id || week.start_date || "";
    const payerParam = filterPayerId ? `&payerId=${filterPayerId}` : "";
    const staffParam =
      Array.isArray(filterStaffIds) && filterStaffIds.length > 0
        ? `&staffIds=${encodeURIComponent(filterStaffIds.join(","))}`
        : filterStaffId
          ? `&staffId=${encodeURIComponent(filterStaffId)}`
          : "";
    // Prevent stale staff totals from the previous request/week.
    setPaystubs([]);
    setPaystubStats({});
    fetchJson(
      `${BASE_URL}/api/accounting/paystubs?${paramKey}=${paramVal}${payerParam}${staffParam}`,
      { signal: controller.signal },
    )
      .then((json) => {
        const listRaw = Array.isArray(json?.data?.paystubs)
          ? json.data.paystubs
          : [];
        const list = listRaw.map((stub) => {
          const directMethod =
            stub?.payment_delivery_method ||
            stub?.cheque_method ||
            stub?.chequeMethod ||
            "";
          return {
            ...stub,
            payment_note:
              stub?.payment_note || mapPaymentMethodLabel(directMethod),
          };
        });
        setPaystubs(list);
        setPaystubStats(json?.data?.stats || {});
        setAccountPayers({});
        setAccountPaymentMethods({});
        if (selectedTab === "PAY STUBS") {
          setTotalResults(list.length);
          setTotalPages(Math.max(1, Math.ceil(list.length / rowsPerPage)));
        }
      })
      .catch((e) => {
        if (e?.name !== "AbortError") {
          console.error("Paystubs refresh failed", e);
        }
      });
    return () => controller.abort();
  }, [week, refreshTick, filterPayerId, filterStaffId, filterStaffIds, selectedTab]);

  // Helper to compute a compact page list with ellipses
  const getPageList = (total, current) => {
    const delta = 2; // neighbors to show
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= total; i++) {
      if (
        i === 1 ||
        i === total ||
        (i >= current - delta && i <= current + delta)
      ) {
        range.push(i);
      }
    }

    for (const i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l > 2) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  const onCloseWeek = async () => {
    try {
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      let targetWeekId = week?.week_id;
      let refStart = filterWeek || selectedDate || week?.start_date || "";
      if (!targetWeekId) {
        const createRes = await fetch(`${BASE_URL}/api/accounting/weeks`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders },
          body: JSON.stringify({ startDate: filterWeek || selectedDate || week?.start_date }),
        });
        const created = await createRes.json();
        targetWeekId = created?.data?.week_id;
        refStart = created?.data?.start_date || refStart;
        setWeek(created?.data || null);
      }
      if (!targetWeekId) return;
      const closeRes = await fetch(
        `${BASE_URL}/api/accounting/weeks/${targetWeekId}/close`,
        {
          method: "POST",
          headers: authHeaders,
        },
      );
      if (!closeRes.ok) throw new Error("Close failed");
      const closeJson = await closeRes.json().catch(() => ({}));
      const closedPayload = closeJson?.data || {};
      // Optimistic local update so UI flips immediately (include close metadata)
      setWeek((prev) =>
        prev
          ? {
              ...prev,
              state: "closed",
              start_date: refStart || prev.start_date,
              close_date:
                closedPayload.close_date ??
                closedPayload.closed_date ??
                prev.close_date,
              close_count:
                closedPayload.close_count ??
                closedPayload.times_closed ??
                (Number(prev.close_count || 0) || 0) + 1,
            }
          : prev,
      );
      const res = await fetch(
        `${BASE_URL}/api/accounting/weeks${refStart ? `?startDate=${refStart}` : ""}`,
      );
      const j = await res.json();
      setWeek(j?.data || null);
      if (refStart) {
        setFilterWeek(refStart);
        setSelectedDate(refStart);
      }
      setRefreshTick((t) => t + 1);
      showSuccess("Week closed");
    } catch (e) {
      console.error(e);
      showError("Failed to close week");
    }
  };

  const onOpenWeek = async () => {
    try {
      const token = localStorage.getItem("token");
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      let targetWeekId = week?.week_id;
      let refStart = filterWeek || selectedDate || week?.start_date || "";
      if (!targetWeekId) {
        const createRes = await fetch(`${BASE_URL}/api/accounting/weeks`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders },
          body: JSON.stringify({ startDate: filterWeek || selectedDate || week?.start_date }),
        });
        const created = await createRes.json();
        targetWeekId = created?.data?.week_id;
        refStart = created?.data?.start_date || refStart;
        setWeek(created?.data || null);
      }
      if (!targetWeekId) return;
      const openRes = await fetch(
        `${BASE_URL}/api/accounting/weeks/${targetWeekId}/open`,
        {
          method: "POST",
          headers: authHeaders,
        },
      );
      if (!openRes.ok) throw new Error("Open failed");
      // Optimistic local update so UI flips immediately
      setWeek((prev) =>
        prev
          ? { ...prev, state: "open", start_date: refStart || prev.start_date }
          : prev,
      );
      const res = await fetch(
        `${BASE_URL}/api/accounting/weeks${refStart ? `?startDate=${refStart}` : ""}`,
      );
      const j = await res.json();
      setWeek(j?.data || null);
      if (refStart) {
        setFilterWeek(refStart);
        setSelectedDate(refStart);
      }
      showSuccess("Week re-opened");
    } catch (e) {
      console.error(e);
      showError("Failed to open week");
    }
  };

  const loadAuditLog = async () => {
    try {
      if (!week?.week_id) return;
      setAuditLoading(true);
      const params = new URLSearchParams({
        weekId: String(week.week_id),
        offset: String(auditOffset || 0),
        limit: String(auditLimit || 25),
      });
      if (auditSearch?.trim()) params.set("search", auditSearch.trim());
      if (auditSubjectType) params.set("subjectType", auditSubjectType);
      if (auditDetailType) params.set("detailType", auditDetailType);
      if (auditActionType) params.set("action", auditActionType);
      params.set(
        "sortDir",
        String(auditSortDirection || "").toLowerCase() === "ascending"
          ? "asc"
          : "desc",
      );
      const j = await fetchJson(
        `${BASE_URL}/api/accounting/audits/rich?${params.toString()}`,
      );
      const payload = j?.data || {};
      const rows = Array.isArray(payload?.audits) ? payload.audits : [];
      setAudits(rows);
      setAuditTotal(Number(payload?.paging?.total || rows.length || 0));
      setShowAudit(true);
    } catch (e) {
      console.error(e);
      setAudits([]);
      setAuditTotal(0);
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (!showAudit) return;
    loadAuditLog();
  }, [
    showAudit,
    week?.week_id,
    auditOffset,
    auditLimit,
    auditSearch,
    auditSubjectType,
    auditDetailType,
    auditActionType,
    auditSortDirection,
    auditRefreshTick,
  ]);

  const clearAuditFilters = () => {
    setAuditSubjectType("");
    setAuditDetailType("");
    setAuditActionType("");
    setAuditShowObjectId(false);
    setAuditSortBy("Created");
    setAuditSortDirection("Descending");
    setAuditOffset(0);
  };

  const openAuditFilterPath = (field = "") => {
    setShowAuditLimitDropdown(false);
    setAuditActiveFilterField(field);
    setShowAuditFilterPanel(true);
  };

  useEffect(() => {
    if (!showAuditFilterPanel || !auditActiveFilterField) return;

    const map = {
      subjectType: auditSubjectTypeRef,
      detailType: auditDetailTypeRef,
      actionType: auditActionTypeRef,
      showObjectId: auditShowObjectIdRef,
      sortBy: auditSortByRef,
      sortDirection: auditSortDirectionRef,
    };

    const targetRef = map[auditActiveFilterField] || null;
    const timer = setTimeout(() => {
      const node = targetRef?.current;
      node?.focus?.();
      node?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 60);

    return () => clearTimeout(timer);
  }, [showAuditFilterPanel, auditActiveFilterField]);

  const openAuditLog = async () => {
    setAuditSearch("");
    clearAuditFilters();
    setAuditOffset(0);
    setShowAuditFilterPanel(false);
    setShowAuditLimitDropdown(false);
    setAuditActiveFilterField("");
    setShowAudit(true);
  };

  const downloadRowCsv = (filename, headers, values) => {
    try {
      const csv = [
        headers.join(","),
        values
          .map((v) =>
            typeof v === "string" ? '"' + v.replace(/"/g, '""') + '"' : v,
          )
          .join(","),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      showError("Failed to export");
    }
  };

  const downloadResponseAsFile = async (response, fallbackFilename) => {
    const blob = await response.blob();
    const cd = response.headers.get("Content-Disposition") || "";
    const match = cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    const serverName = match
      ? decodeURIComponent(match[1] || match[2] || "")
      : "";
    const filename = serverName || fallbackFilename;
    const urlObj = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = urlObj;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(urlObj);
  };

  // Comprehensive export functions
  const exportWeeks = async (format = "csv") => {
    try {
      if (week?.state !== "closed") {
        showError("Close the week to export.");
        return;
      }
      const paramKey = week?.week_id ? "weekId" : "startDate";
      const paramVal = week?.week_id || week?.start_date || "";
      const response = await fetch(
        `${BASE_URL}/api/accounting/export/weeks?format=${format}&${paramKey}=${paramVal}`,
      );
      await downloadResponseAsFile(
        response,
        `accounting_weeks_${week?.start_date || ""}_${week?.end_date || ""}.${format}`,
      );
      showSuccess("Weeks data exported successfully");
    } catch (e) {
      console.error(e);
      showError("Failed to export weeks data");
    }
  };

  const exportPaystubs = async (format = "csv", accountId = null) => {
    try {
      if (week?.state !== "closed") {
        showError("Close the week to export.");
        return;
      }
      const paramKey = week?.week_id ? "weekId" : "startDate";
      const paramVal = week?.week_id || week?.start_date || "";
      let url = `${BASE_URL}/api/accounting/export/paystubs?format=${format}&${paramKey}=${paramVal}`;
      if (accountId) url += `&accountId=${accountId}`;
      else if (Array.isArray(filterStaffIds) && filterStaffIds.length > 0) {
        url += `&staffIds=${encodeURIComponent(filterStaffIds.join(","))}`;
      } else if (filterStaffId) {
        url += `&staffId=${encodeURIComponent(filterStaffId)}`;
      }
      if (filterPayerId) {
        url += `&payerId=${encodeURIComponent(filterPayerId)}`;
      }
      const response = await fetch(url);
      await downloadResponseAsFile(
        response,
        accountId
          ? `paystub_${accountId}_${week?.start_date || ""}.${format}`
          : `paystubs_${week?.start_date || ""}_${week?.end_date || ""}.${format}`,
      );
      showSuccess("Paystub exported successfully");
    } catch (e) {
      console.error(e);
      showError("Failed to export paystub");
    }
  };

  const exportPayroll = async (format = "csv") => {
    try {
      if (week?.state !== "closed") {
        showError("Close the week to export.");
        return;
      }
      const paramKey = week?.week_id ? "weekId" : "startDate";
      const paramVal = week?.week_id || week?.start_date || "";
      const payerParam = filterPayerId ? `&payerId=${filterPayerId}` : "";
      const response = await fetch(
        `${BASE_URL}/api/accounting/export/payroll?format=${format}&${paramKey}=${paramVal}${payerParam}`,
      );
      const ext = format === "excel" ? "xlsx" : format;
      const fallback = `payroll_${week?.end_date || ""}.${ext}`;
      await downloadResponseAsFile(response, fallback);
      showSuccess("Payroll summary exported successfully");
    } catch (e) {
      console.error(e);
      showError("Failed to export payroll summary");
    }
  };

  const exportClientReports = async (
    format = "pdf",
    clientId = null,
    clientName = null,
  ) => {
    try {
      if (week?.state !== "closed") {
        showError("Close the week to export.");
        return;
      }
      const paramKey = week?.week_id ? "weekId" : "startDate";
      const paramVal = week?.week_id || week?.start_date || "";
      let url = `${BASE_URL}/api/accounting/export/clients?format=${format}&${paramKey}=${paramVal}`;
      if (clientId) url += `&clientId=${clientId}`;
      else if (Array.isArray(filterClientIds) && filterClientIds.length > 0) {
        url += `&clientIds=${encodeURIComponent(filterClientIds.join(","))}`;
      } else if (filterClientId) {
        url += `&clientId=${encodeURIComponent(filterClientId)}`;
      }
      const response = await fetch(url);
      if (!response.ok) {
        const msg = await response.text().catch(() => "");
        throw new Error(msg || `Export failed (${response.status})`);
      }
      const contentType = String(response.headers.get("Content-Type") || "").toLowerCase();
      if (format === "pdf" && !contentType.includes("application/pdf")) {
        throw new Error("Server did not return a valid PDF file");
      }
      if (format === "zip" && !contentType.includes("application/zip")) {
        throw new Error("Server did not return a valid ZIP file");
      }
      const weekStart = week?.start_date || filterDate || "";
      const resolvedClientName =
        clientName ||
        (clients || []).find(
          (c) => Number(c.client_id) === Number(clientId),
        )?.name ||
        "";
      const fallback = clientId
        ? buildClientReportPdfFilename(resolvedClientName, weekStart)
        : buildClientReportsZipFilename(weekStart);
      await downloadResponseAsFile(response, fallback);
      showSuccess("Client report exported successfully");
    } catch (e) {
      console.error(e);
      showError("Failed to export client report");
    }
  };

  const exportAdjustments = async (format = "csv", adjustmentId = null) => {
    try {
      let url = `${BASE_URL}/api/accounting/export/adjustments?format=${format}`;
      if (adjustmentId) url += `&adjustmentId=${adjustmentId}`;
      const response = await fetch(url);
      const fallback = adjustmentId
        ? `adjustment_${adjustmentId}.${format}`
        : `adjustments_${new Date().toISOString().slice(0, 10)}.${format}`;
      await downloadResponseAsFile(response, fallback);
      showSuccess("Adjustment exported successfully");
    } catch (e) {
      console.error(e);
      showError("Failed to export adjustment");
    }
  };

  const deletePayer = async (payerId) => {
    try {
      const response = await fetch(
        `${BASE_URL}/api/accounting/payers/${payerId}/delete`,
        { method: "PUT" },
      );

      const result = await response.json();

      if (response.ok && result.success) {
        showSuccess("Payer deleted successfully");

        setPayers((prev) =>
          (prev || []).map((p) =>
            Number(p.payer_id) === Number(payerId)
              ? { ...p, state: "deleted" }
              : p,
          ),
        );

        setRefreshTick((t) => t + 1);
      } else {
        showError(result.message || "Failed to delete payer");
      }
    } catch (e) {
      console.error(e);
      showError("Failed to delete payer");
    }
  };

  const restorePayer = async (payerId) => {
    try {
      let response = await fetch(
        `${BASE_URL}/api/accounting/payers/${payerId}/restore`,
        { method: "PUT" },
      );
      // Backward compatibility for old route shape.
      if (response.status === 404) {
        response = await fetch(
          `${BASE_URL}/api/accounting/payers/${payerId}?softRestore=1`,
          { method: "PUT" },
        );
      }
      const result = await response.json().catch(() => ({}));
      if (response.ok && result?.success) {
        showSuccess("Payer restored successfully");
        setPayers((prev) =>
          (prev || []).map((p) =>
            Number(p.payer_id) === Number(payerId)
              ? { ...p, state: "active" }
              : p,
          ),
        );
        setRefreshTick((t) => t + 1);
      } else {
        showError(result?.message || "Failed to restore payer");
      }
    } catch (e) {
      console.error(e);
      showError("Failed to restore payer");
    }
  };

  const printContent = (title, lines, orientation = "portrait") => {
    const w = window.open("", "_blank");
    if (!w) return;
    const pageCss =
      orientation === "landscape"
        ? "@page { size: A4 landscape; margin: 20mm 12mm; }"
        : "@page { size: A4 portrait; margin: 20mm 12mm; }";
    w.document.write(
      "<html><head><title>" +
        title +
        "</title><style>" +
        pageCss +
        " body{font-family:sans-serif}</style></head><body>",
    );
    lines.forEach((line) => w.document.write("<div>" + line + "</div>"));
    w.document.write("</body></html>");
    w.document.close();
    w.focus();
    w.print();
    w.close();
  };

  // Comprehensive print functions
  const printWeeks = (orientation = "portrait") => {
    if (week?.state !== "closed") {
      showError("Close the week to print.");
      return;
    }
    const lines = [
      "Week Period: " +
        (week?.start_date || "") +
        " - " +
        (week?.end_date || ""),
      "Week Status: " + (week?.state || ""),
      "Total Staff: " + (stats?.shift_assigned_users_count || 0),
      "Total Pay: $" + (stats?.total_pay_before_tax || 0).toFixed(2),
      "",
      "Field Staff Summary:",
    ];

    fieldstaffSummary.forEach((f) => {
      lines.push(
        "  " +
          f.name +
          ": $" +
          (f.pay || 0) +
          " (Payer: " +
          (f.payer?.name || "N/A") +
          ")",
      );
    });

    lines.push("");
    lines.push("Client Summary:");

    clientSummary.forEach((c) => {
      const bill = c.bill || 0;
      const pay = c.pay || 0;
      const margin =
        bill > 0 ? Math.round(((bill - pay) / bill) * 1000) / 10 : 0;
      lines.push(
        "  " +
          c.name +
          ": Bill $" +
          (c.bill || 0) +
          ", Pay $" +
          (c.pay || 0) +
          ", Margin " +
          margin +
          "%",
      );
    });

    printContent("Accounting Weeks Report", lines, orientation);
  };

  const escapePrintHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const resolvePrintAvatarSrc = (group) =>
    accountAvatarImgSrc(group?.account_id, group) || "";

  const buildPrintAvatarHtml = (group, displayName) => {
    const src = resolvePrintAvatarSrc(group);
    const initial =
      escapePrintHtml(
        String(displayName || group?.staff_name || group?.name || "?")
          .trim()
          .slice(0, 1),
      ) || "?";
    if (src) {
      const safeSrc = escapePrintHtml(src);
      return `<div class="avatar-wrap">
        <img class="avatar avatar-img" src="${safeSrc}" alt=""
          onerror="this.style.display='none';var f=this.nextElementSibling;if(f)f.style.display='flex';" />
        <div class="avatar avatar-fallback" style="display:none">${initial}</div>
      </div>`;
    }
    return `<div class="avatar-wrap"><div class="avatar avatar-fallback">${initial}</div></div>`;
  };

  const waitForPrintImages = (doc) =>
    new Promise((resolve) => {
      const images = Array.from(doc.querySelectorAll("img"));
      if (!images.length) {
        resolve();
        return;
      }
      let pending = images.length;
      let settled = false;
      const done = () => {
        if (settled) return;
        pending -= 1;
        if (pending <= 0) {
          settled = true;
          resolve();
        }
      };
      images.forEach((img) => {
        if (img.complete) {
          done();
          return;
        }
        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });
      });
      setTimeout(() => {
        if (settled) return;
        settled = true;
        resolve();
      }, 30000);
    });

  // Envelope icon is for mail delivery only — hide for pickup / deposit / unset.
  const showPaymentEnvelopeIcon = (note = "") => {
    const value = String(note || "").trim().toLowerCase();
    return value.includes("mailed") || value === "cheque to be mailed";
  };

  const printPaystubs = (orientation = "portrait") => {
    if (week?.state !== "closed") {
      showError("Close the week to print.");
      return;
    }
    const pageCss =
      orientation === "landscape"
        ? "@page { size: letter landscape; margin: 0.5in; }"
        : "@page { size: letter portrait; margin: 0.5in; }";
    const safeNum = (v) => {
      const n = parseFloat(v || 0);
      return Number.isFinite(n) ? n.toFixed(2) : "0.00";
    };
    const formatPrintDate = (value) => {
      if (!value) return "";
      const raw = String(value).trim();
      const date =
        /^\d{4}-\d{2}-\d{2}$/.test(raw.slice(0, 10)) && raw.length <= 10
          ? parseDbDateTime(`${raw.slice(0, 10)}T12:00:00Z`)
          : parseDbDateTime(raw);
      if (!date) return escapePrintHtml(raw);
      return escapePrintHtml(
        formatAppIntlLabel(date, {
          month: "long",
          day: "numeric",
          year: "numeric",
        }),
      );
    };
    const bankIconSvg =
      '<svg viewBox="0 0 24 24" width="12" height="12"><path fill="#111827" d="M3 21h18v-2H3v2zm2-4h2v-7H5v7zm4 0h2v-7H9v7zm3-14L2 8v2h20V8L12 3zm1 14h2v-7h-2v7zm4 0h2v-7h-2v7z"/></svg>';
    const calendarIconSvg =
      '<svg viewBox="0 0 24 24" width="12" height="12"><path fill="#111827" d="M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 15H5V10h14v9zm0-11H5V6h14v2z"/></svg>';
    const envelopeIconSvg =
      '<svg viewBox="0 0 24 24" width="12" height="12"><path fill="#64748b" d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4-8 5L4 8V6l8 5 8-5v2z"/></svg>';

    const groupsHtml = (filteredPaystubs || [])
      .map((group) => {
        const staffName = escapePrintHtml(
          group.staff_name || "Unknown Staff",
        );
        const staffAddress = escapePrintHtml(group.staff_address || "");
        const invoiceTo = escapePrintHtml(
          group.invoice_submitted_to || group.payer_name || "-",
        );
        const payPeriod = escapePrintHtml(
          formatPayPeriodLabel(
            group.pay_period_start || week?.start_date,
            group.pay_period_end || week?.end_date,
          ),
        );
        const paymentNoteRaw = group.payment_note || "";
        const paymentNote = escapePrintHtml(paymentNoteRaw);
        const showEnvelope = showPaymentEnvelopeIcon(paymentNoteRaw);
        const linesHtml = (group.lines || [])
          .map((line) => {
            const travelHours = accountingLineTravelHours(line);
            const travelAmount = accountingLineTravelAmount(line);
            const travelRate = accountingLineTravelRate(line);
            const shiftTotal = accountingLineShiftTotal(line);
            const hasTravel = travelAmount > 0;
            return `
              <tr class="${hasTravel ? "shift-row-with-travel" : ""}">
                <td>${escapePrintHtml(line.row_number || "")}</td>
                <td>${formatPrintDate(line.date)}</td>
                <td>${escapePrintHtml(line.client_name || "")}<div class="worked-by">${escapePrintHtml(line.worked_by || "")}</div></td>
                <td class="right">${safeNum(line.hours)}</td>
                <td class="right">$${safeNum(line.rate)}</td>
                <td class="right">$${safeNum(shiftTotal)}</td>
              </tr>
              ${
                hasTravel
                  ? `<tr class="travel-row">
                      <td></td>
                      <td></td>
                      <td></td>
                      <td class="right">${safeNum(travelHours)}</td>
                      <td class="right">$${safeNum(travelRate)}</td>
                      <td class="right">$${safeNum(travelAmount)}</td>
                    </tr>`
                  : ""
              }
            `;
          })
          .join("");

        return `
          <section class="group">
            <div class="group-head">
              <div class="identity">
                ${buildPrintAvatarHtml(group, group.staff_name)}
                <div>
                  <div class="staff-name">${staffName}</div>
                  ${staffAddress ? `<div class="muted">${staffAddress}</div>` : ""}
                </div>
              </div>
              <div class="meta">
                <div class="meta-block">
                  <div class="meta-line">
                    <span class="icon" aria-hidden="true">${bankIconSvg}</span>
                    <span class="label">Invoice Submitted to:</span>
                  </div>
                  <div class="meta-value">${invoiceTo}</div>
                </div>
                <div class="meta-block">
                  <div class="meta-line">
                    <span class="icon" aria-hidden="true">${calendarIconSvg}</span>
                    <span class="label">Pay Period:</span>
                  </div>
                  <div class="meta-value">${payPeriod}</div>
                </div>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Client</th>
                  <th class="right">Hours</th>
                  <th class="right">Rate</th>
                  <th class="right">Total</th>
                </tr>
              </thead>
              <tbody>
                ${linesHtml}
              </tbody>
            </table>
            <div class="group-foot">
              <div class="foot-note">
                ${
                  paymentNote
                    ? `${showEnvelope ? `<span class="icon" aria-hidden="true">${envelopeIconSvg}</span>` : ""}<span>${paymentNote}</span>`
                    : ""
                }
              </div>
              <div class="foot-totals">
                <div>Total of ${safeNum(group.total_hours)} shift hours${Number(group.adjustment_total || 0) !== 0 ? " plus adjustments" : ""} : $${safeNum(group.footer_subtotal || group.subtotal)}</div>
                ${
                  group.hst_number
                    ? `<div>HST #${escapePrintHtml(group.hst_number)} (${safeNum(group.hst_percent)}%): $${safeNum(group.hst_amount)}</div>`
                    : ""
                }
                <div class="grand-total">Total: $${safeNum(group.total_with_hst || group.footer_subtotal || group.subtotal)}</div>
              </div>
            </div>
          </section>
        `;
      })
      .join("");

    const html = `
      <html>
        <head>
          <title>Pay Stubs - ${week?.start_date || ""} - ${week?.end_date || ""}</title>
          <style>
            ${pageCss}
            html, body { margin: 0; padding: 0; }
            body {
              font-family: Arial, Helvetica, sans-serif;
              color: #334155;
              font-size: ${orientation === "landscape" ? "11px" : "12px"};
              line-height: 1.35;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .group {
              margin: 0 0 12px;
              padding: ${orientation === "landscape" ? "22px 0 10px" : "26px 0 10px"};
              border-bottom: 1px solid #d9dee7;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .group:first-child { padding-top: 0; }
            .group-head {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 16px;
              margin-bottom: 8px;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .identity {
              display: flex;
              align-items: flex-start;
              gap: 10px;
              min-width: 0;
              flex: 1 1 auto;
            }
            .avatar-wrap { flex: 0 0 40px; }
            .avatar {
              width: 40px;
              height: 40px;
              border-radius: 9999px;
              flex-shrink: 0;
            }
            .avatar-img {
              object-fit: cover;
              display: block;
            }
            .avatar-fallback {
              background: #f3f4f6;
              color: #6b7280;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 14px;
              font-weight: 500;
            }
            .staff-name {
              color: #1f2937;
              font-size: ${orientation === "landscape" ? "15px" : "16px"};
              font-weight: 400;
              line-height: 1.25;
            }
            .muted {
              color: #64748b;
              font-size: 11px;
              line-height: 1.3;
              margin-top: 2px;
            }
            .meta {
              display: flex;
              align-items: flex-start;
              gap: ${orientation === "landscape" ? "28px" : "22px"};
              flex: 0 0 auto;
            }
            .meta-block { min-width: ${orientation === "landscape" ? "150px" : "130px"}; text-align: left; }
            .meta-line {
              display: flex;
              align-items: center;
              gap: 6px;
              font-size: 11px;
              color: #334155;
            }
            .meta-line .label { font-weight: 400; }
            .icon {
              width: 12px;
              height: 12px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              flex: 0 0 12px;
            }
            .icon svg { display: block; width: 12px; height: 12px; }
            .meta-value {
              font-size: 11px;
              color: #334155;
              margin-top: 2px;
              line-height: 1.3;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 4px;
              table-layout: fixed;
            }
            thead { display: table-header-group; }
            thead th {
              text-align: left;
              font-size: 11px;
              color: #6b7280;
              background: #f3f4f6;
              padding: 8px 10px;
              font-weight: 400;
            }
            tbody td {
              padding: 8px 10px;
              font-size: 11px;
              border-bottom: 1px solid #cbd5e1;
              vertical-align: top;
            }
            thead th:nth-child(1), tbody td:nth-child(1) { width: 28px; }
            thead th:nth-child(2), tbody td:nth-child(2) { width: ${orientation === "landscape" ? "140px" : "124px"}; }
            thead th:nth-child(4), tbody td:nth-child(4),
            thead th:nth-child(5), tbody td:nth-child(5) { width: 72px; }
            thead th:nth-child(6), tbody td:nth-child(6) { width: 92px; }
            .worked-by {
              color: #64748b;
              font-size: 10px;
              margin-top: 2px;
            }
            .shift-row-with-travel td {
              border-bottom: 0;
              padding-bottom: 0;
            }
            .travel-row td {
              padding-top: 0;
              padding-bottom: 8px;
              transform: translateY(-3px);
            }
            .right { text-align: right; }
            .group-foot {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              gap: 12px;
              margin-top: 8px;
              font-size: 11px;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .foot-note {
              color: #64748b;
              display: flex;
              align-items: center;
              gap: 6px;
              padding-top: 2px;
            }
            .foot-totals {
              margin-left: auto;
              text-align: right;
              color: #334155;
            }
            .foot-totals > div { margin-top: 2px; }
            .foot-totals > div:first-child { margin-top: 0; }
            .grand-total {
              font-weight: 700;
              color: #111827;
            }
          </style>
        </head>
        <body>
          ${groupsHtml}
        </body>
      </html>
    `;

    try {
      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.left = "-10000px";
      iframe.style.top = "0";
      iframe.style.width = "1024px";
      iframe.style.height = "768px";
      iframe.style.border = "0";
      iframe.setAttribute("aria-hidden", "true");
      document.body.appendChild(iframe);
      const idoc = iframe.contentWindow.document;
      idoc.open();
      idoc.write(html);
      idoc.close();

      waitForPrintImages(idoc)
        .then(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (e) {
            console.error("Print failed", e);
            showError("Printing failed");
          }
          setTimeout(() => {
            try {
              document.body.removeChild(iframe);
            } catch (e) {}
          }, 1000);
        })
        .catch((e) => {
          console.error("Print image preload error", e);
          showError("Printing is not available");
          try {
            document.body.removeChild(iframe);
          } catch (err) {}
        });
    } catch (e) {
      console.error("Print iframe error", e);
      showError("Printing is not available");
    }
  };

  const printPayroll = (orientation = "portrait") => {
    if (week?.state !== "closed") {
      showError("Close the week to print.");
      return;
    }
    const pageCss =
      orientation === "landscape"
        ? "@page { size: letter landscape; margin: 0.5in; }"
        : "@page { size: letter portrait; margin: 0.5in; }";
    const safeNum = (v) => {
      const n = parseFloat(v || 0);
      return Number.isFinite(n) ? n.toFixed(2) : "0.00";
    };
    const payPeriodLabel = escapePrintHtml(
      formatPayPeriodLabel(week?.start_date, week?.end_date),
    );
    const bankIconSvg =
      '<svg viewBox="0 0 24 24" width="12" height="12"><path fill="#111827" d="M3 21h18v-2H3v2zm2-4h2v-7H5v7zm4 0h2v-7H9v7zm3-14L2 8v2h20V8L12 3zm1 14h2v-7h-2v7zm4 0h2v-7h-2v7z"/></svg>';
    const calendarIconSvg =
      '<svg viewBox="0 0 24 24" width="12" height="12"><path fill="#111827" d="M19 4h-1V2h-2v2H8V2H6v2H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 15H5V10h14v9zm0-11H5V6h14v2z"/></svg>';

    const groupsHtml = (filteredFieldstaffSummary || [])
      .map((group) => {
        const lines = Array.isArray(group.lines) ? group.lines : [];
        const staffName = escapePrintHtml(group.name || "");
        const payoutStaffText = String(group.name || "").trim();
        const rowsHtml =
          lines.length > 0
            ? lines
                .map((line) => {
                  const workedByText = String(
                    line.workedByName ||
                      line.worked_by ||
                      line.workedBy ||
                      "",
                  ).trim();
                  const showWorkedBy =
                    workedByText &&
                    workedByText.toLowerCase() !==
                      payoutStaffText.toLowerCase();
                  return `
                    <tr>
                      <td>${showWorkedBy ? escapePrintHtml(workedByText) : ""}</td>
                      <td>${escapePrintHtml(line.description || "")}</td>
                      <td class="right">${safeNum(line.quantity)}</td>
                      <td class="right">$${safeNum(line.rate)}</td>
                      <td class="right">$${safeNum(line.total)}</td>
                    </tr>
                  `;
                })
                .join("")
            : `
                <tr>
                  <td>${staffName}</td>
                  <td>No line items</td>
                  <td class="right"></td>
                  <td class="right"></td>
                  <td class="right"></td>
                </tr>
              `;

        return `
          <section class="group">
            <div class="group-head">
              <div class="identity">
                ${buildPrintAvatarHtml(group, group.name)}
                <div>
                  <div class="staff-name">${staffName}</div>
                  <div class="muted">Staff ID · ${escapePrintHtml(group.account_id || "-")}</div>
                </div>
              </div>
              <div class="meta">
                <div class="meta-block">
                  <div class="meta-line">
                    <span class="icon" aria-hidden="true">${bankIconSvg}</span>
                    <span class="label">Payer</span>
                  </div>
                  <div class="meta-value">${escapePrintHtml(group.payer?.name || "—")}</div>
                </div>
                <div class="meta-block">
                  <div class="meta-line">
                    <span class="icon" aria-hidden="true">${calendarIconSvg}</span>
                    <span class="label">Pay Period:</span>
                  </div>
                  <div class="meta-value">${payPeriodLabel}</div>
                </div>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Staff Who Worked</th>
                  <th>Description</th>
                  <th class="right">Quantity</th>
                  <th class="right">Rate</th>
                  <th class="right">Total</th>
                </tr>
              </thead>
              <tbody>
                ${rowsHtml}
              </tbody>
            </table>
            <div class="group-total">Total: $${safeNum(group.total || group.pay || 0)}</div>
          </section>
        `;
      })
      .join("");

    const html = `
      <html>
        <head>
          <title>Payroll Summary - Week of ${week?.start_date || ""}</title>
          <style>
            ${pageCss}
            html, body { margin: 0; padding: 0; }
            body {
              font-family: Arial, Helvetica, sans-serif;
              color: #1f2937;
              font-size: ${orientation === "landscape" ? "11px" : "12px"};
              line-height: 1.35;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .group {
              margin: 0 0 14px;
              padding: ${orientation === "landscape" ? "18px 0 10px" : "22px 0 10px"};
              border-bottom: 1px solid #d1d5db;
              break-inside: auto;
              page-break-inside: auto;
            }
            .group:first-child { padding-top: 0; }
            .group-head {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 16px;
              margin-bottom: 8px;
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .identity {
              display: flex;
              align-items: flex-start;
              gap: 10px;
              min-width: 0;
              flex: 1 1 auto;
            }
            .avatar-wrap { flex: 0 0 40px; }
            .avatar {
              width: 40px;
              height: 40px;
              border-radius: 9999px;
              flex-shrink: 0;
            }
            .avatar-img {
              object-fit: cover;
              display: block;
            }
            .avatar-fallback {
              background: #f3f4f6;
              color: #6b7280;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 14px;
              font-weight: 500;
            }
            .staff-name {
              font-size: ${orientation === "landscape" ? "15px" : "16px"};
              font-weight: 400;
              color: #1f2937;
              line-height: 1.25;
            }
            .muted {
              color: #6b7280;
              font-size: 11px;
              line-height: 1.3;
              margin-top: 2px;
            }
            .meta {
              display: flex;
              align-items: flex-start;
              gap: ${orientation === "landscape" ? "28px" : "22px"};
              flex: 0 0 auto;
            }
            .meta-block {
              min-width: ${orientation === "landscape" ? "130px" : "120px"};
              text-align: left;
            }
            .meta-line {
              display: flex;
              align-items: center;
              gap: 6px;
              font-size: 11px;
              color: #334155;
            }
            .icon {
              width: 12px;
              height: 12px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              flex: 0 0 12px;
            }
            .icon svg { display: block; width: 12px; height: 12px; }
            .meta-value {
              font-size: 11px;
              color: #111827;
              margin-top: 2px;
              line-height: 1.3;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
              margin-top: 4px;
              break-inside: auto;
              page-break-inside: auto;
            }
            thead { display: table-header-group; }
            thead th {
              background: #eeeeee;
              color: #6b7280;
              font-size: 11px;
              font-weight: 400;
              padding: 8px 10px;
              text-align: left;
            }
            tbody tr {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            tbody td {
              font-size: 11px;
              padding: 8px 10px;
              vertical-align: top;
              border-bottom: 1px solid #f3f4f6;
            }
            thead th:nth-child(1), tbody td:nth-child(1) { width: 18%; }
            thead th:nth-child(3), tbody td:nth-child(3),
            thead th:nth-child(4), tbody td:nth-child(4),
            thead th:nth-child(5), tbody td:nth-child(5) { width: 12%; }
            .right { text-align: right; }
            .group-total {
              margin-top: 8px;
              text-align: right;
              font-size: 11px;
              font-weight: 700;
              color: #111827;
              break-inside: avoid;
              page-break-inside: avoid;
            }
          </style>
        </head>
        <body>
          ${groupsHtml}
        </body>
      </html>
    `;

    try {
      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.left = "-10000px";
      iframe.style.top = "0";
      iframe.style.width = "1024px";
      iframe.style.height = "768px";
      iframe.style.border = "0";
      iframe.setAttribute("aria-hidden", "true");
      document.body.appendChild(iframe);

      const idoc = iframe.contentWindow.document;
      idoc.open();
      idoc.write(html);
      idoc.close();

      waitForPrintImages(idoc)
        .then(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch (e) {
            console.error("Print failed", e);
            showError("Printing failed");
          }
          setTimeout(() => {
            try {
              document.body.removeChild(iframe);
            } catch (e) {}
          }, 1000);
        })
        .catch((e) => {
          console.error("Print image preload error", e);
          showError("Printing is not available");
          try {
            document.body.removeChild(iframe);
          } catch (err) {}
        });
    } catch (e) {
      console.error("Print iframe error", e);
      showError("Printing is not available");
    }
  };

  const printClientReports = (orientation = "portrait") => {
    if (week?.state !== "closed") {
      showError("Close the week to print.");
      return;
    }
    const lines = [
      "Client Reports - Week of " + (week?.start_date || ""),
      "",
      "Client Summary:",
    ];

    clientSummary.forEach((c) => {
      const bill = c.bill || 0;
      const pay = c.pay || 0;
      const margin =
        bill > 0 ? Math.round(((bill - pay) / bill) * 1000) / 10 : 0;
      lines.push("  " + c.name + ":");
      lines.push("    Bill: $" + (c.bill || 0));
      lines.push("    Pay: $" + (c.pay || 0));
      lines.push("    Margin: " + margin + "%");
    });

    printContent("Client Reports", lines, orientation);
  };

  const printAdjustments = () => {
    const lines = [
      "Adjustments Report - " + formatAppDate(new Date()),
      "",
      "Adjustments:",
      ...adjustments.map((a) =>
        [
          "  ID: " + a.adjustment_id,
          "  Subject: " + (a.subject_name || "Unknown"),
          "  Type: " + a.subject_type,
          "  Date: " + (a.recognition_date?.slice(0, 10) || ""),
          "  Quantity: " + (a.quantity || 0) + ", Rate: $" + (a.amount || 0),
          "  Total: $" + (a.total || 0),
          "  Description: " + (a.description || "No description"),
          "",
        ].join("\n"),
      ),
    ];
    printContent("Adjustments Report", lines);
  };

  // Legacy functions - now use comprehensive functions
  const exportPaystubsCsv = () => exportPaystubs("csv");
  const exportPayrollCsv = () => exportPayroll("csv");

  // Fetch data for selected tab
  // useEffect(() => {
  //   const fetchData = async () => {
  //     try {
  //       const endpoint = selectedTab.toLowerCase().replace(" ", "_"); // e.g., "pay_stubs"
  //       const url = `${BASE_URL}/api/accounting/${endpoint}?page=${currentPage}&limit=${rowsPerPage}&search=${searchTerm}`;
  //       const res = await fetch(url);
  //       console.log("📡 Fetching from:", url);

  //       const data = await res.json();
  //       console.log("✅ Backend response:", data);

  //       setAccountingData(Array.isArray(data.data) ? data.data : []);
  //       setTotalPages(data.totalPages || 1);
  //     } catch (err) {
  //       console.error(err);
  //       setAccountingData([]);
  //     }
  //   };
  //   fetchData();
  // }, [selectedTab, currentPage, searchTerm]);

  // useEffect(() => {
  //   if (selectedTab === "WEEKS") {
  //       const fetchSummary = async () => {
  //       try {
  //           const res = await fetch(`${BASE_URL}/api/accounting/summary`);
  //           const data = await res.json();
  //           setSummary(data);
  //       } catch (err) {
  //           console.error("Error fetching summary:", err);
  //           setSummary(null);
  //       }
  //       };
  //       fetchSummary();
  //   }
  //   }, [selectedTab]);

  useEffect(() => setCurrentPage(1), [selectedTab, searchTerm]);

  useEffect(() => {
    let cancelled = false;
    const loadWeekFilterOptions = async () => {
      try {
        if (
          selectedTab !== "WEEKS" &&
          selectedTab !== "PAY STUBS" &&
          selectedTab !== "PAYROLL SUMMARY" &&
          selectedTab !== "CLIENT REPORTS"
        )
          return;
        const json = await fetchJson(`${BASE_URL}/api/accounting/staff-clients`);
        if (cancelled || !json) return;
        const rows = Array.isArray(json?.data) ? json.data : [];
        setWeekFilterClients(
          rows
            .filter((row) => String(row.kind || "").toLowerCase() === "client")
            .map((row) => ({ id: Number(row.id), name: row.name || "" })),
        );
        setWeekFilterStaff(
          rows
            .filter((row) => String(row.kind || "").toLowerCase() === "account")
            .filter((row) => {
              const type = String(row.type || "").toLowerCase();
              return (
                !type ||
                type === "fieldstaff" ||
                type === "outsourcedstaff" ||
                type.includes("field") ||
                type.includes("outsourced")
              );
            })
            .map((row) => ({ id: Number(row.id), name: row.name || "" })),
        );
      } catch (e) {
        console.error("Failed to load WEEKS filter options", e);
      }
    };
    loadWeekFilterOptions();
    return () => {
      cancelled = true;
    };
  }, [selectedTab]);

  // Load payer list when PAY STUBS or PAYROLL SUMMARY tab active
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (
          selectedTab !== "PAY STUBS" &&
          selectedTab !== "PAYROLL SUMMARY"
        )
          return;
        if (payerList && payerList.length > 0) return;
        const url = `${BASE_URL}/api/accounting/payers?status=active`;
        const res = await fetch(url);
        const json = await res.json();
        if (cancelled) return;
        const rows = Array.isArray(json.data) ? json.data : [];
        setPayerList(rows);
        const lookup = rows.reduce((acc, payer) => {
          if (payer?.payer_id) acc[payer.payer_id] = payer.name || `#${payer.payer_id}`;
          return acc;
        }, {});
        setSelectedPayerLookup(lookup);
      } catch (e) {
        console.error("Failed to load payers", e);
        setPayerList([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedTab]);



  // When paystubs are loaded, fetch account -> payer_id mapping for any missing accounts
  useEffect(() => {
    const missing = (paystubs || [])
      .map((p) => p.account_id)
      .filter(
        (v, i, a) =>
          a.indexOf(v) === i &&
          (!Object.prototype.hasOwnProperty.call(accountPayers, v) ||
            !Object.prototype.hasOwnProperty.call(accountPaymentMethods, v)),
      );
    if (missing.length === 0) return;
    let cancelled = false;
    const load = async () => {
      try {
        const cacheBust = Date.now();
        const promises = missing.map((id) =>
          fetchJson(`${BASE_URL}/api/personnel/accounts/${id}?_=${cacheBust}`, {
            cache: "no-store",
          }),
        );
        const results = await Promise.all(promises);
        if (cancelled) return;
        const payerMap = { ...accountPayers };
        const paymentMethodMap = { ...accountPaymentMethods };
        results.forEach((r) => {
          if (r && r.data && r.data.account_id) {
            payerMap[r.data.account_id] = r.data.payer_id || null;
            paymentMethodMap[r.data.account_id] =
              r.data.payment_delivery_method || r.data.cheque_method || "";
          }
        });
        setAccountPayers(payerMap);
        setAccountPaymentMethods(paymentMethodMap);
      } catch (e) {
        console.error("Failed to fetch account payer mapping", e);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [paystubs, accountPayers, accountPaymentMethods]);

  const mapPaymentMethodLabel = (method = "") => {
    const value = String(method || "").trim().toLowerCase();
    if (
      value === "delivery_mail" ||
      value === "deliverymail" ||
      value === "mail" ||
      value.includes("mail")
    ) {
      return "Cheque to be mailed";
    }
    if (
      value === "delivery_pickup" ||
      value === "deliverypickup" ||
      value === "pickup" ||
      value.includes("pickup")
    ) {
      return "Cheque to be picked up at the office";
    }
    if (
      value === "direct_deposit" ||
      value === "directdeposit" ||
      value === "deposit" ||
      value.includes("deposit")
    ) {
      return "Direct deposit";
    }
    return "";
  };

  const resolvePaymentNote = (paystub) => {
    if (paystub?.payment_note) return paystub.payment_note;
    const directFromPaystub = mapPaymentMethodLabel(
      paystub?.payment_delivery_method ||
        paystub?.cheque_method ||
        paystub?.chequeMethod ||
        paystub?.paymentMethod,
    );
    if (directFromPaystub) return directFromPaystub;
    const accountId = Number(paystub?.account_id);
    if (!Number.isFinite(accountId) || accountId <= 0) return "";
    return mapPaymentMethodLabel(accountPaymentMethods[accountId]);
  };

  // Helpers: case-insensitive includes. The whole query is tried first (original
  // behaviour), then word by word in any order, so "aramark oak" matches
  // "Aramark - Ford Oakville Assembly" and "pia zuniga" a name with a middle part.
  const includes = (v, q) => {
    const value = String(v || "").toLowerCase();
    const query = String(q || "").toLowerCase();
    if (value.includes(query)) return true;
    const words = query.split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w));
    return words.length > 1 && words.every((w) => value.includes(w));
  };
  const isDeletedState = (state) => {
    const s = String(state || "").trim().toLowerCase();
    return s === "deleted" || s === "delete";
  };
  const statsValue = (camelKey, snakeKey, fallback = 0) => {
    const source = stats || {};
    const value =
      typeof source?.[camelKey] !== "undefined"
        ? source[camelKey]
        : source?.[snakeKey];
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };
  const byDir = (a, b, dir) => (dir === "asc" ? a - b : b - a);
  const byStr = (a, b, dir) => {
    const A = String(a || "").toLowerCase();
    const B = String(b || "").toLowerCase();
    if (A < B) return dir === "asc" ? -1 : 1;
    if (A > B) return dir === "asc" ? 1 : -1;
    return 0;
  };

  // Derive filtered/sorted datasets for current tab views
  const getVal = (obj, key) => {
    switch (key) {
      // Weeks → Client Summary
      case "clientName":
        return obj?.name;
      case "bill":
        return Number(obj?.bill || 0);
      case "pay":
        return Number(obj?.pay || 0);
      case "margin":
        return (
          (Number(obj?.bill || 0) - Number(obj?.pay || 0)) /
          (Number(obj?.bill || 0) || 1)
        );
      // Weeks → Payroll Summary (fieldstaff)
      case "staffName":
        return obj?.name;
      case "payerName":
        return obj?.payer?.name;
      // Pay Stubs
      case "pst_staff":
        return obj?.staff_name;
      case "pst_reg_h":
        return Number(obj?.regular_hours || 0);
      case "pst_ot_h":
        return Number(obj?.overtime_hours || 0);
      case "pst_tot_h":
        return Number(obj?.total_hours || 0);
      case "pst_reg_pay":
        return Number(obj?.regular_pay || 0);
      case "pst_ot_pay":
        return Number(obj?.overtime_pay || 0);
      case "pst_gross":
        return Number(obj?.gross_pay || 0);
      case "pst_hst":
        return Number(obj?.hst_deduction || 0);
      case "pst_tax":
        return Number(obj?.tax_deduction || 0);
      case "pst_net":
        return Number(obj?.net_pay || 0);
      // Clients
      case "client_name":
        return obj?.name;
      case "parent_name":
        return obj?.parent_client?.name;
      // Adjustments
      case "adj_id":
        return Number(obj?.adjustment_id || 0);
      case "adj_subject":
        return obj?.subject_name;
      case "adj_type":
        return obj?.subject_type;
      case "adj_date":
        return obj?.recognition_date;
      case "adj_qty":
        return Number(obj?.quantity || 0);
      case "adj_rate":
        return Number(obj?.amount || 0);
      case "adj_total":
        return Number(obj?.total || 0);
      case "adj_desc":
        return obj?.description;
      // Payers
      case "payer_name":
        return obj?.name;
      case "payer_worker_type":
        return obj?.worker_type;
      case "payer_legal":
        return obj?.legal_name;
      // Alternate payer keys used by filter panel
      case "worker_type":
        return obj?.worker_type;
      case "legal_name":
        return obj?.legal_name;
      default:
        return obj?.name;
    }
  };

  const sortGeneric = (arr) =>
    arr.slice().sort((a, b) => {
      const A = getVal(a, sortKey);
      const B = getVal(b, sortKey);
      if (typeof A === "number" && typeof B === "number")
        return byDir(A, B, sortDir);
      return byStr(A, B, sortDir);
    });

  // Filter chip helpers
  const removeFilter = (type) => {
    switch (type) {
      case "client":
        setFilterClient("");
        setFilterClientId(null);
        setFilterClientIds([]);
        break;
      case "staff":
        setFilterStaff("");
        setFilterStaffId(null);
        setFilterStaffIds([]);
        break;
      case "payer":
        setFilterPayer("");
        setFilterPayerId(null);
        break;
      case "week":
        seededDefaultWeekFilterRef.current = true;
        setFilterWeek("");
        setWeek(null);
        break;
      case "date":
        setFilterDate(
          selectedTab === "CLIENT REPORTS"
            ? accountingLocalYyyyMmDd()
            : "",
        );
        setWeek(null);
        break;
      case "fromDate":
        setFilterFromDate("");
        break;
      case "toDate":
        setFilterToDate("");
        break;
      case "subject":
        setFilterSubject("");
        break;
      case "showDeleted":
        setFilterShowDeleted(false);
        break;
      case "search":
        setSearchTerm("");
        break;
    }
    setRefreshTick((t) => t + 1);
  };

  const clearAllFilters = async () => {
    seededDefaultWeekFilterRef.current = false;
    setFilterClient("");
    setFilterStaff("");
    setFilterClientId(null);
    setFilterClientIds([]);
    setFilterStaffId(null);
    setFilterStaffIds([]);
    setFilterPayerId(null);
    setFilterDate(
      selectedTab === "CLIENT REPORTS" ? accountingLocalYyyyMmDd() : "",
    );
    setFilterPayer("");

    setFilterFromDate("");
    setFilterToDate("");
    setFilterSubject("");
    setFilterShowDeleted(false);
    setSearchTerm("");
    setSortKey("name");
    setSortDir("asc");
    setSelectedStaffLookup({});

    if (accountingTabUsesWeekFilter(selectedTab)) {
      try {
        const wJson = await fetchJson(`${BASE_URL}/api/accounting/weeks`);
        const defaultWeek = wJson?.data || null;
        if (defaultWeek?.start_date) {
          setFilterWeek(defaultWeek.start_date);
          setSelectedDate(defaultWeek.start_date);
          setSelectedYear(
            new Date(`${defaultWeek.start_date}T12:00:00`).getFullYear(),
          );
        } else {
          setFilterWeek("");
        }
        setWeek(defaultWeek);
      } catch (e) {
        console.error("Failed to reset week to default in clearAllFilters", e);
        setFilterWeek("");
        setWeek(null);
      }
    } else {
      setFilterWeek("");
      setWeek(null);
    }

    setRefreshTick((t) => t + 1);
  };

  const formatWeekCloseLabel = (closeDate, closeCount) => {
    if (closeDate == null || closeDate === "") return "";
    const rawCloseDate = String(closeDate).trim();
    if (!rawCloseDate || rawCloseDate.startsWith("0000-00-00")) return "";
    const dt = parseDbDateTime(rawCloseDate.replace(" ", "T"));
    if (!dt) return "";
    const timeText = formatAppTime(dt);
    const dateText = formatAppIntlLabel(dt, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    const ordinal = (n) => {
      const v = Number(n) || 0;
      const mod100 = v % 100;
      if (mod100 >= 11 && mod100 <= 13) return `${v}th`;
      switch (v % 10) {
        case 1:
          return `${v}st`;
        case 2:
          return `${v}nd`;
        case 3:
          return `${v}rd`;
        default:
          return `${v}th`;
      }
    };
    return `${ordinal(closeCount || 1)} time at ${timeText} ${dateText}`;
  };

  const getAllActiveFilters = () => {
    const filters = [];
    const summarizeFilterNames = (names) => {
      if (!Array.isArray(names) || names.length === 0) return "";
      if (names.length <= 2) return names.join(", ");
      return `${names.slice(0, 2).join(", ")}..`;
    };
    const selectedClientNames = (Array.isArray(filterClientIds) ? filterClientIds : [])
      .map((id) => {
        const match = (weekFilterClients || []).find(
          (client) => Number(client.id) === Number(id),
        );
        return match?.name || `#${id}`;
      })
      .filter(Boolean);
    const selectedStaffNames = (Array.isArray(filterStaffIds) ? filterStaffIds : [])
      .map((id) => {
        if (selectedStaffLookup[id]) return selectedStaffLookup[id];
        const match = (weekFilterStaff || []).find(
          (staff) => Number(staff.id) === Number(id),
        );
        const display = getStaffDisplayName(match);
        return display || `#${id}`;
      })
      .filter(Boolean);
    if (searchTerm)
      filters.push({ type: "search", label: `Search: ${searchTerm}` });
    if (selectedClientNames.length > 0) {
      filters.push({
        type: "client",
        label: `Clients: ${summarizeFilterNames(selectedClientNames)}`,
      });
    }
    if (filterClient)
      filters.push({ type: "client", label: `Client: ${filterClient}` });
    if (selectedStaffNames.length > 0) {
      filters.push({
        type: "staff",
        label: `Staff: ${summarizeFilterNames(selectedStaffNames)}`,
      });
    }
    if (filterStaff)
      filters.push({ type: "staff", label: `Staff: ${filterStaff}` });
    if (filterPayer)
      filters.push({ type: "payer", label: `Payer: ${filterPayer}` });
    if (
      filterWeek &&
      selectedTab !== "ADJUSTMENT" &&
      selectedTab !== "PAYERS" &&
      selectedTab !== "CLIENT REPORTS"
    ) {
      filters.push({
        type: "week",
        label: formatWeekChipLabel(filterWeek, week?.end_date),
      });
    }
    if (filterDate && selectedTab === "CLIENT REPORTS") {
      filters.push({
        type: "date",
        label: formatDateChipLabel(filterDate),
      });
    }
    if (filterFromDate)
      filters.push({
        type: "fromDate",
        label: formatDateChipLabel(filterFromDate).replace(/^Date: /, "From: "),
        isPermanent: true,
      });
    if (filterToDate)
      filters.push({
        type: "toDate",
        label: formatDateChipLabel(filterToDate).replace(/^Date: /, "To: "),
        isPermanent: true,
      });
    if (filterSubject)
      filters.push({ type: "subject", label: `Subject: ${filterSubject}` });
    if (filterShowDeleted)
      filters.push({ type: "showDeleted", label: "Show Deleted" });
    return filters;
  };

  const currentAccountingFilterChips = useMemo(() => {
    if (selectedTab === "WEEKS") {
      return buildAccountingWeeksFilterChips({
        searchTerm,
        innerTab,
        filterClientIds,
        filterStaffIds,
        filterWeek,
        weekData: week,
        weekFilterClients,
        weekFilterStaff,
        selectedStaffLookup,
      });
    }
    if (selectedTab === "PAY STUBS") {
      return buildAccountingPayStubsFilterChips({
        searchTerm,
        filterPayer,
        filterWeek,
        weekData: week,
        filterStaffIds,
        weekFilterStaff,
        selectedStaffLookup,
      });
    }
    if (selectedTab === "PAYROLL SUMMARY") {
      return buildAccountingPayrollSummaryFilterChips({
        searchTerm,
        filterPayer,
        filterWeek,
        weekData: week,
        filterStaffIds,
        weekFilterStaff,
        selectedStaffLookup,
      });
    }
    if (selectedTab === "CLIENT REPORTS") {
      return buildAccountingClientReportsFilterChips({
        searchTerm,
        filterDate,
        filterClientIds,
        weekFilterClients,
      });
    }
    if (selectedTab === "ADJUSTMENT") {
      return buildAccountingAdjustmentFilterChips({
        searchTerm,
        filterFromDate,
        filterToDate,
        filterSubject,
        filterShowDeleted,
      });
    }
    if (selectedTab === "PAYERS") {
      return buildAccountingPayersFilterChips({
        searchTerm,
        filterShowDeleted,
      });
    }

    return getAllActiveFilters().map((f) => ({
      id: f.type,
      key: f.type,
      fieldId: f.type,
      label: f.label,
      removable: !f.isPermanent,
    }));
  }, [
    selectedTab,
    innerTab,
    searchTerm,
    filterClientIds,
    filterStaffIds,
    filterWeek,
    week,
    weekFilterClients,
    weekFilterStaff,
    selectedStaffLookup,
    filterClient,
    filterStaff,
    filterPayer,
    filterPayerId,
    filterDate,
    filterFromDate,
    filterToDate,
    filterSubject,
    filterShowDeleted,
  ]);

  const renderAccountingFilterFields = () => {
    switch (selectedTab) {
      case "WEEKS":
        return (
          <AccountingWeeksFilterFields
            focusedFieldId={filterPanel.focusedFieldId}
            onFocusClear={filterPanel.clearFocusedField}
            innerTab={innerTab}
            filterClientIds={filterClientIds}
            setFilterClientIds={setFilterClientIds}
            filterStaffIds={filterStaffIds}
            setFilterStaffIds={setFilterStaffIds}
            selectedStaffLookup={selectedStaffLookup}
            setSelectedStaffLookup={setSelectedStaffLookup}
            filterWeek={filterWeek}
            setFilterWeek={setFilterWeek}
            weekData={week}
            sortKey={sortKey}
            setSortKey={setSortKey}
            sortDir={sortDir}
            setSortDir={setSortDir}
            weekFilterClients={weekFilterClients}
            weekFilterStaff={weekFilterStaff}
          />
        );
      case "PAY STUBS":
        return (
          <AccountingPayStubsFilterFields
            focusedFieldId={filterPanel.focusedFieldId}
            onFocusClear={filterPanel.clearFocusedField}
            filterPayer={filterPayer}
            filterPayerId={filterPayerId}
            setFilterPayer={setFilterPayer}
            setFilterPayerId={setFilterPayerId}
            filterWeek={filterWeek}
            setFilterWeek={setFilterWeek}
            weekData={week}
            filterStaffIds={filterStaffIds}
            setFilterStaffIds={setFilterStaffIds}
            selectedStaffLookup={selectedStaffLookup}
            setSelectedStaffLookup={setSelectedStaffLookup}
            payerList={payerList}
            weekFilterStaff={weekFilterStaff}
          />
        );
      case "PAYROLL SUMMARY":
        return (
          <AccountingPayrollSummaryFilterFields
            focusedFieldId={filterPanel.focusedFieldId}
            onFocusClear={filterPanel.clearFocusedField}
            filterPayer={filterPayer}
            filterPayerId={filterPayerId}
            setFilterPayer={setFilterPayer}
            setFilterPayerId={setFilterPayerId}
            filterWeek={filterWeek}
            setFilterWeek={setFilterWeek}
            weekData={week}
            filterStaffIds={filterStaffIds}
            setFilterStaffIds={setFilterStaffIds}
            selectedStaffLookup={selectedStaffLookup}
            setSelectedStaffLookup={setSelectedStaffLookup}
            payerList={payerList}
            weekFilterStaff={weekFilterStaff}
          />
        );
      case "CLIENT REPORTS":
        return (
          <AccountingClientReportsFilterFields
            focusedFieldId={filterPanel.focusedFieldId}
            onFocusClear={filterPanel.clearFocusedField}
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            filterClientIds={filterClientIds}
            setFilterClientIds={setFilterClientIds}
            weekFilterClients={weekFilterClients}
          />
        );
      case "ADJUSTMENT":
        return (
          <AccountingAdjustmentFilterFields
            focusedFieldId={filterPanel.focusedFieldId}
            onFocusClear={filterPanel.clearFocusedField}
            filterFromDate={filterFromDate}
            setFilterFromDate={setFilterFromDate}
            filterToDate={filterToDate}
            setFilterToDate={setFilterToDate}
            filterSubject={filterSubject}
            setFilterSubject={setFilterSubject}
            filterShowDeleted={filterShowDeleted}
            setFilterShowDeleted={setFilterShowDeleted}
            sortKey={sortKey}
            setSortKey={setSortKey}
            sortDir={sortDir}
            setSortDir={setSortDir}
          />
        );
      case "PAYERS":
        return (
          <AccountingPayersFilterFields
            focusedFieldId={filterPanel.focusedFieldId}
            onFocusClear={filterPanel.clearFocusedField}
            filterShowDeleted={filterShowDeleted}
            setFilterShowDeleted={setFilterShowDeleted}
            sortKey={sortKey}
            setSortKey={setSortKey}
            sortDir={sortDir}
            setSortDir={setSortDir}
          />
        );
      default:
        return null;
    }
  };

  const renderToolbarActions = () => {
    switch (selectedTab) {
      case "WEEKS":
        return (
          <div className="ml-2 flex items-center gap-2">
            {!isClosedWeek && (
              <button
                type="button"
                onClick={onCloseWeek}
                className={ATLAS_TOOLBAR_BTN_PRIMARY}
              >
                Close Week
              </button>
            )}
            {isClosedWeek && (
              <>
                <button
                  type="button"
                  onClick={onOpenWeek}
                  className={ATLAS_TOOLBAR_BTN_PRIMARY}
                >
                  Re-Open Week
                </button>
                <button
                  type="button"
                  onClick={openAuditLog}
                  className={ATLAS_TOOLBAR_BTN_SECONDARY}
                >
                  View Week History
                </button>
              </>
            )}
          </div>
        );
      case "PAY STUBS":
        return (
          <ActionDropdown
            trigger="button"
            label="Print"
            title="Print"
            buttonVariant="primary"
            disabled={!isClosedWeek}
            actions={[
              {
                label: "Portrait",
                onClick: () => printPaystubs("portrait"),
              },
              {
                label: "Landscape",
                onClick: () => printPaystubs("landscape"),
              },
            ]}
          />
        );
      case "PAYROLL SUMMARY":
        return (
          <div className="flex items-center gap-2">
            <ActionDropdown
              trigger="button"
              label="Print"
              title="Print"
              buttonVariant="primary"
              disabled={!isClosedWeek}
              actions={[
                {
                  label: "Portrait",
                  onClick: () => printPayroll("portrait"),
                },
                {
                  label: "Landscape",
                  onClick: () => printPayroll("landscape"),
                },
              ]}
            />
            <ActionDropdown
              trigger="button"
              label="Export"
              title="Export"
              disabled={!isClosedWeek}
              actions={[
                {
                  label: "Export CSV",
                  onClick: () => exportPayroll("csv"),
                },
                {
                  label: "Export Excel",
                  onClick: () => exportPayroll("xlsx"),
                },
              ]}
            />
          </div>
        );
      case "CLIENT REPORTS":
        return (
          <button
            type="button"
            onClick={() => {
              if (!isClosedWeek) {
                showError("Cannot Generate Report For Open Weeks");
                return;
              }
              exportClientReports("zip");
            }}
            className={ATLAS_TOOLBAR_BTN_PRIMARY}
          >
            DOWNLOAD ALL
          </button>
        );
      case "ADJUSTMENT":
        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOpenModal("adjustment")}
              className={ATLAS_TOOLBAR_BTN_PRIMARY}
            >
              Add
            </button>
            <AddAdjustmentMoal
              isOpen={openModal === "adjustment"}
              onClose={() => setOpenModal(null)}
              onPayerAdded={() => {
                setOpenModal(null);
                setRefreshTick((t) => t + 1);
              }}
            />
            <EditAdjustmentModal
              isOpen={showEditAdjustmentModal}
              onClose={() => {
                setShowEditAdjustmentModal(false);
                setEditAdjustment(null);
              }}
              adjustment={editAdjustment}
              onSaved={(updated) => {
                if (updated?.adjustment_id) {
                  setAdjustments((prev) => {
                    let found = false;
                    const merged = (prev || []).map((a) => {
                      if (
                        Number(a.adjustment_id) ===
                        Number(updated.adjustment_id)
                      ) {
                        found = true;
                        return {
                          ...a,
                          ...updated,
                          subject_name:
                            updated.subject_name || a.subject_name || "",
                          subject_type:
                            updated.subject_type || a.subject_type || "",
                        };
                      }
                      return a;
                    });
                    return found ? merged : [updated, ...merged];
                  });
                }
                setShowEditAdjustmentModal(false);
                setEditAdjustment(null);
                setRefreshTick((prev) => prev + 1);
                if (typeof window !== "undefined") {
                  try {
                    sessionStorage.setItem(
                      "accounting_selected_tab",
                      "ADJUSTMENT",
                    );
                  } catch (e) {}
                  setTimeout(() => {
                    window.location.reload();
                  }, 50);
                }
              }}
              isWeekClosed={!isClosedWeek}
            />
          </div>
        );
      case "PAYERS":
        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setOpenModal("payer")}
              className={ATLAS_TOOLBAR_BTN_PRIMARY}
            >
              CREATE
            </button>
            <AddPayerModal
              isOpen={openModal === "payer"}
              onClose={() => setOpenModal(null)}
              onPayerAdded={() => {
                setOpenModal(null);
                setRefreshTick((t) => t + 1);
              }}
            />
          </div>
        );
      default:
        return null;
    }
  };

  // Deduplicate client summary by client_id (safeguard against duplicate API responses)
  const uniqueClientSummary = (clientSummary || []).filter(
    (r, idx, arr) => arr.findIndex((x) => x.client_id === r.client_id) === idx,
  );
  const weekClosedLabel = formatWeekCloseLabel(
    week?.close_date ?? week?.closed_date ?? week?.last_modified,
    week?.close_count ?? week?.times_closed,
  );
  const submittedActualsPercent = (() => {
    const activeShifts = Number(
      stats?.activeShiftsCount ?? stats?.active_shifts_count ?? 0,
    );
    const submittedNonconflict = Number(
      stats?.shiftActualsSubmittedNonconflictCount ??
        stats?.shift_actuals_submitted_nonconflict_count ??
        0,
    );
    if (activeShifts > 0) {
      return (submittedNonconflict / activeShifts) * 100;
    }
    return Number(
      stats?.actualsSubmittedPercent ??
        stats?.shift_actuals_submitted_nonconflict_percentage ??
        0,
    );
  })();

  const filteredClientSummary = sortGeneric(
    uniqueClientSummary.filter((r) => {
      const selectedIds = new Set(
        [
          ...(Array.isArray(filterClientIds) ? filterClientIds : []),
          filterClientId,
        ]
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0),
      );
      const clientMatch =
        selectedIds.size > 0
          ? selectedIds.has(Number(r.client_id))
          : includes(r.name, filterClient || "");
      return clientMatch && includes(r.name, searchTerm);
    }),
  );
  const weeksClientTotal = filteredClientSummary.length;
  const weeksClientTotalPages = Math.max(
    1,
    Math.ceil(weeksClientTotal / rowsPerPage),
  );
  const paginatedFilteredClientSummary = filteredClientSummary.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );
  const totalClientBill = (filteredClientSummary || []).reduce(
    (sum, r) => sum + Number(r?.bill || 0),
    0,
  );
  const totalClientPay = (filteredClientSummary || []).reduce(
    (sum, r) => sum + Number(r?.pay || 0),
    0,
  );
  const totalClientMarginDollar = totalClientBill - totalClientPay;
  const totalClientMarginPercent =
    totalClientBill > 0 ? (totalClientMarginDollar / totalClientBill) * 100 : 0;

  // Deduplicate fieldstaff summary by account_id (safeguard against duplicate API responses)
  const uniqueFieldstaffSummary = (fieldstaffSummary || []).filter(
    (r, idx, arr) =>
      arr.findIndex((x) => x.account_id === r.account_id) === idx,
  );

  const filteredFieldstaffSummary = sortGeneric(
    uniqueFieldstaffSummary.filter((r) => {
      const staffMatch = filterStaffId
        ? Number(r.account_id) === Number(filterStaffId)
        : includes(r.name, filterStaff || "");
      return (
        staffMatch &&
        (includes(r.name, searchTerm) || includes(r.payer?.name, searchTerm))
      );
    }),
  );
  const weeksStaffTotal = filteredFieldstaffSummary.length;
  const weeksStaffTotalPages = Math.max(
    1,
    Math.ceil(weeksStaffTotal / rowsPerPage),
  );
  const paginatedFilteredFieldstaffSummary = filteredFieldstaffSummary.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );
  const totalFieldstaffPay = (filteredFieldstaffSummary || []).reduce(
    (sum, r) => sum + Number(r?.pay || 0),
    0,
  );
  const closedWeekStatsBill = Number(
    stats?.totalBillBeforeTax ?? stats?.total_bill_before_tax ?? 0,
  );
  const closedWeekStatsPay = Number(
    stats?.totalPayBeforeTax ?? stats?.total_pay_before_tax ?? 0,
  );
  const closedWeekBill =
    selectedTab === "WEEKS" &&
    String(week?.state || "").toLowerCase() === "closed" &&
    closedWeekStatsBill > 0
      ? closedWeekStatsBill
      : totalClientBill;
  const totalWeekPay =
    selectedTab === "WEEKS" &&
    String(week?.state || "").toLowerCase() === "closed"
      ? closedWeekStatsPay > 0
        ? closedWeekStatsPay
        : totalFieldstaffPay
      : totalClientPay;
  const totalWeekMarginDollar = closedWeekBill - totalWeekPay;
  const totalWeekMarginPercent =
    closedWeekBill > 0 ? (totalWeekMarginDollar / closedWeekBill) * 100 : 0;

  const filteredPaystubs = (Array.isArray(paystubs) ? paystubs : []).reduce(
    (acc, paystub) => {
      const resolvedPaymentNote = resolvePaymentNote(paystub);
      const allLines = Array.isArray(paystub?.lines) ? paystub.lines : [];
      const matchingLines = allLines.filter((line) => {
        return (
          includes(line?.client_name, searchTerm) ||
          includes(line?.worked_by, searchTerm) ||
          includes(line?.date, searchTerm)
        );
      });

      const headerMatches =
        !searchTerm ||
        includes(paystub?.staff_name, searchTerm) ||
        includes(paystub?.staff_address, searchTerm) ||
        includes(paystub?.payer_name, searchTerm) ||
        includes(paystub?.invoice_submitted_to, searchTerm) ||
        includes(resolvedPaymentNote, searchTerm);

      if (!headerMatches && matchingLines.length === 0) {
        return acc;
      }

      acc.push({
        ...paystub,
        payment_note: resolvedPaymentNote,
        lines: headerMatches ? allLines : matchingLines,
      });
      return acc;
    },
    [],
  );
  const paginatedFilteredPaystubs = filteredPaystubs.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  useEffect(() => {
    if (selectedTab === "PAY STUBS") {
      setTotalResults(filteredPaystubs.length);
      const pages = Math.max(1, Math.ceil(filteredPaystubs.length / rowsPerPage));
      setTotalPages(pages);
      if (currentPage > pages) {
        setCurrentPage(1);
      }
    }
  }, [filteredPaystubs.length, selectedTab, rowsPerPage, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [innerTab, searchTerm]);

  useEffect(() => {
    if (selectedTab === "WEEKS") {
      const total = innerTab === "Client Summary" ? weeksClientTotal : weeksStaffTotal;
      const pages = Math.max(1, Math.ceil(total / rowsPerPage));
      if (currentPage > pages) {
        setCurrentPage(1);
      }
    }
  }, [selectedTab, innerTab, weeksClientTotal, weeksStaffTotal, rowsPerPage, currentPage]);
  const paystubDisplayStats =
    selectedTab === "PAY STUBS" &&
    String(week?.state || "").toLowerCase() !== "closed"
      ? {
          accounts: 0,
          shifts: 0,
          hours: 0,
          subtotal: 0,
          validActualsPercent: 0,
        }
      : paystubStats || {};

  const filteredClients = sortGeneric(
    (clients || []).filter((c) => {
      const selectedIds = new Set(
        [
          ...(Array.isArray(filterClientIds) ? filterClientIds : []),
          filterClientId,
        ]
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id) && id > 0),
      );
      const clientMatch =
        selectedIds.size > 0
          ? selectedIds.has(Number(c.client_id))
          : true;
      const searchMatch =
        !searchTerm ||
        includes(c.name, searchTerm) ||
        includes(c.parent_client?.name, searchTerm);
      return clientMatch && searchMatch;
    }),
  );

  const clientReportsTotal = filteredClients.length;
  const clientReportsTotalPages = Math.max(
    1,
    Math.ceil(clientReportsTotal / rowsPerPage),
  );
  const paginatedFilteredClients =
    selectedTab === "CLIENT REPORTS"
      ? filteredClients.slice(
          (currentPage - 1) * rowsPerPage,
          currentPage * rowsPerPage,
        )
      : filteredClients;

  const filteredAdjustments = sortGeneric(
    (adjustments || []).filter((a) => {
      // text search
      const textMatch =
        includes(a.subject_name, searchTerm) ||
        includes(a.description, searchTerm);
      // subject type filter
      let subjectMatch = true;
      if (filterSubject) {
        const t = String(a.subject_type || "").toLowerCase();
        if (filterSubject === "staff") subjectMatch = t.includes("staff");
        else if (filterSubject === "client")
          subjectMatch = t.includes("client");
        else
          subjectMatch = String(a.subject_type || "") === String(filterSubject);
      }
      // date range filter
      let dateMatch = true;
      if (filterFromDate) {
        dateMatch =
          dateMatch &&
          (a.recognition_date || "").slice(0, 10) >= filterFromDate;
      }
      if (filterToDate) {
        dateMatch =
          dateMatch && (a.recognition_date || "").slice(0, 10) <= filterToDate;
      }
      // show deleted
      const deletedOk = filterShowDeleted
        ? isDeletedState(a.state)
        : !isDeletedState(a.state);
      return textMatch && subjectMatch && dateMatch && deletedOk;
    }),
  );

  const filteredPayers = sortGeneric(
    (payers || []).filter((p) => {
      const textMatch =
        includes(p.name, searchTerm) ||
        includes(p.worker_type, searchTerm) ||
        includes(p.legal_name, searchTerm);
      const deletedOk = filterShowDeleted
        ? isDeletedState(p.state)
        : !isDeletedState(p.state);
      return textMatch && deletedOk;
    }),
  );

  const payersListTotal = filteredPayers.length;
  const payersListTotalPages = Math.max(
    1,
    Math.ceil(payersListTotal / rowsPerPage),
  );
  const paginatedFilteredPayers = filteredPayers.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const effectiveListTotal =
    selectedTab === "CLIENT REPORTS"
      ? clientReportsTotal
      : selectedTab === "PAYERS"
        ? payersListTotal
        : selectedTab === "WEEKS"
          ? (innerTab === "Client Summary" ? weeksClientTotal : weeksStaffTotal)
          : Number(totalResults || 0);
  const effectiveListTotalPages =
    selectedTab === "CLIENT REPORTS"
      ? clientReportsTotalPages
      : selectedTab === "PAYERS"
        ? payersListTotalPages
        : selectedTab === "WEEKS"
          ? (innerTab === "Client Summary" ? weeksClientTotalPages : weeksStaffTotalPages)
          : Number(totalPages || 1);

  const deleteAdjustment = async (adjustment) => {
    try {
      const response = await fetch(
        `${BASE_URL}/api/accounting/adjustments/${adjustment.adjustment_id}/delete`,
        { method: "PUT" },
      );
      const result = await response.json().catch(() => ({}));
      if (response.ok && result?.success) {
        showSuccess("Adjustment deleted successfully");
        setAdjustments((prev) =>
          (prev || []).map((a) =>
            Number(a.adjustment_id) === Number(adjustment.adjustment_id)
              ? { ...a, state: "deleted" }
              : a,
          ),
        );
        setRefreshTick((t) => t + 1);
      } else {
        showError(result?.message || "Failed to delete adjustment");
      }
    } catch (e) {
      console.error(e);
      showError("Failed to delete adjustment");
    }
  };

  const restoreAdjustment = async (adjustment) => {
    try {
      const response = await fetch(
        `${BASE_URL}/api/accounting/adjustments/${adjustment.adjustment_id}/restore`,
        { method: "PUT" },
      );
      const result = await response.json().catch(() => ({}));
      if (response.ok && result?.success) {
        showSuccess("Adjustment restored successfully");
        setAdjustments((prev) =>
          (prev || []).map((a) =>
            Number(a.adjustment_id) === Number(adjustment.adjustment_id)
              ? { ...a, state: "active" }
              : a,
          ),
        );
        setRefreshTick((t) => t + 1);
      } else {
        showError(result?.message || "Failed to restore adjustment");
      }
    } catch (e) {
      console.error(e);
      showError("Failed to restore adjustment");
    }
  };

  const ADJUSTMENT_SORT_LABELS = {
    adj_id: "ID",
    adj_date: "Date",
    adj_subject: "Subject",
    adj_type: "Type",
    adj_qty: "Qty",
    adj_rate: "Rate",
    adj_total: "Total",
    adj_desc: "Description",
  };

  const PAYER_SORT_LABELS = {
    name: "Name",
    worker_type: "Worker Type",
    payer_worker_type: "Worker Type",
    legal_name: "Legal Name",
    payer_legal: "Legal Name",
  };

  const accountingToolbarResultsMeta = useMemo(() => {
    if (selectedTab !== "ADJUSTMENT" && selectedTab !== "PAYERS") return null;
    const total = effectiveListTotal;
    const visibleOnPage =
      selectedTab === "ADJUSTMENT"
        ? filteredAdjustments.length
        : selectedTab === "PAYERS"
          ? paginatedFilteredPayers.length
          : 0;
    const startRow = total === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const endRow =
      total === 0
        ? 0
        : visibleOnPage > 0
          ? Math.min(startRow + visibleOnPage - 1, total)
          : Math.min(currentPage * rowsPerPage, total);
    const adjustmentSortOptions = [
      { value: "adj_id", label: "ID" },
      { value: "adj_date", label: "Date" },
    ];
    const payerSortOptions = [
      { value: "name", label: "Name" },
      { value: "worker_type", label: "Worker Type" },
    ];
    const sortOptions =
      selectedTab === "ADJUSTMENT" ? adjustmentSortOptions : payerSortOptions;
    const sortByLabel =
      selectedTab === "ADJUSTMENT"
        ? ADJUSTMENT_SORT_LABELS[sortKey] ||
          adjustmentSortOptions.find((o) => o.value === sortKey)?.label ||
          "ID"
        : PAYER_SORT_LABELS[sortKey] ||
          payerSortOptions.find((o) => o.value === sortKey)?.label ||
          "Name";
    return {
      startRow,
      endRow,
      totalCount: total,
      pageSize: rowsPerPage,
      pageSizeOptions: [15, 25, 50, 100, 200],
      onPageSizeChange: (size) => {
        setRowsPerPage(size);
        setCurrentPage(1);
      },
      showSort: true,
      sortBy: sortKey,
      sortOrder: sortDir,
      sortOptions,
      sortByLabel,
      onSortByChange: setSortKey,
      onSortOrderToggle: () =>
        setSortDir((prev) => (prev === "asc" ? "desc" : "asc")),
    };
  }, [
    selectedTab,
    effectiveListTotal,
    currentPage,
    rowsPerPage,
    sortKey,
    sortDir,
    filteredAdjustments.length,
    paginatedFilteredPayers.length,
  ]);

  const SortArrow = ({ colKey }) => (
    <button
      onClick={() => {
        if (sortKey === colKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
        else {
          setSortKey(colKey);
          setSortDir("asc");
        }
      }}
      className="float-right mr-1 text-sm text-white/90 hover:text-white"
      title="Sort"
    >
      {sortKey === colKey ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
    </button>
  );

  // Normalize week state once for UI logic.
  // Adjustment tab uses API-provided week_status for the currently filtered period.
  const isClosedWeek = String(week?.state || "").toLowerCase() === "closed";
  const adjustmentWeekStatus = String(
    (Array.isArray(adjustments) && adjustments.length > 0
      ? adjustments[0]?.week_status
      : "") || "",
  ).toLowerCase();
  const isAdjustmentWeekClosed = adjustmentWeekStatus === "closed";

  const adjustmentDescriptionColWidth = useMemo(() => {
    const headerLen = "Line Item Description".length;
    const emptyLabel = "No description";
    let maxLen = headerLen;
    for (const a of filteredAdjustments) {
      const text =
        a.description && String(a.description).trim()
          ? String(a.description).trim()
          : emptyLabel;
      const firstLine = text.split(/\r?\n/)[0] || text;
      maxLen = Math.max(maxLen, firstLine.length);
    }
    const minCh = 12;
    const maxCh = filterPanel.isOpen ? 22 : 36;
    const ch = Math.min(maxCh, Math.max(minCh, maxLen + 1));
    return `${ch}ch`;
  }, [filteredAdjustments, filterPanel.isOpen]);

  return (
    <Layout active="Accounting">
      <div className="py-3 px-2 flex flex-col min-h-screen no-mobile-table-stack">
        <AtlasPageHeader title="Accounting" />
        <AtlasPageDivider />

        {/* Client Detail Modal (opens when clicking a client name) */}
        {showClientDetailModal && (
          <ClientDetailModal
            client={detailClient}
            isOpen={showClientDetailModal}
            onClose={() => setShowClientDetailModal(false)}
            selectedTab={"CLIENTS"}
            onSaved={() => setShowClientDetailModal(false)}
          />
        )}

        {showPersonnelDetailModal && (
          <PersonnelDetailModal
            person={detailPerson}
            type={"temp"}
            isOpen={showPersonnelDetailModal}
            onClose={() => {
              setShowPersonnelDetailModal(false);
              setDetailPerson(null);
            }}
          />
        )}

        {showEditPayerModal && (
          <EditPayerModal
            isOpen={showEditPayerModal}
            payer={editPayer}
            onClose={() => {
              setShowEditPayerModal(false);
              setEditPayer(null);
            }}
            onSaved={() => {
              setShowEditAdjustmentModal(false);
              setEditAdjustment(null);
            }}
          />
        )}

        <div className="mt-6 mx-6 p-4 border-2 border-gray-200 rounded-xl h-full relative min-w-0 max-w-full">
          {/* API error banner removed per request */}
          {/* Tabs */}
          <AtlasTabBar tabs={tabs} activeTab={selectedTab} onTabChange={handleTabChange} />

          <fieldset
            disabled={accountingReadOnly}
            className="border-0 m-0 p-0 min-w-0 flex flex-col flex-1 min-h-0"
          >
            <div className={`min-w-0 max-w-full flex flex-col flex-1 min-h-0 ${filterPanel.contentOffsetClass}`}>
          {/* Search and Right-side Filters/Actions */}
          <AtlasListToolbar
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search"
            onFilterClick={filterPanel.toggle}
            filterOpen={filterPanel.isOpen}
            onRefresh={() => setRefreshTick((t) => t + 1)}
            refreshSpinning={loadingRefresh}
            resultsMeta={accountingToolbarResultsMeta ?? undefined}
            actions={renderToolbarActions()}
            chipBar={
              <AtlasFilterChipBar
                chips={currentAccountingFilterChips}
                onChipClick={(chip) => {
                  if (chip.id === "search") {
                    searchInputRef.current?.focus?.();
                  } else {
                    if (chip.fieldId) filterPanel.focusField(chip.fieldId);
                    else filterPanel.open();
                  }
                }}
                onRemove={(chip) => removeFilter(chip.key)}
                onClearAll={clearAllFilters}
              />
            }
          />

          {/* Status card Weeks */}
          {selectedTab === "WEEKS" && (
            <div className="flex flex-wrap items-stretch mb-6 gap-3">
              {!isClosedWeek && (
                <>
                  <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[180px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                    <span className="text-sm text-[#2c3b52] leading-tight">
                      Status
                    </span>
                    <span className="text-base text-[#DC1F00] font-semibold">
                      Open
                    </span>
                  </div>
                  <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[180px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                    <span className="text-sm text-[#2c3b52] leading-tight">
                      Actual Shift Hours
                    </span>
                    <span className="text-base text-[#DC1F00] font-semibold">
                      {formatAccountingStatDecimal(
                        stats?.activeShiftHoursTotal || 0,
                        2,
                      )}
                    </span>
                  </div>
                  <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[220px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                    <span className="text-sm text-[#2c3b52] leading-tight">
                      Actuals Submitted & Not In Conflict
                    </span>
                    <span className="text-base text-[#DC1F00] font-semibold">
                      {formatAccountingStatDecimal(
                        submittedActualsPercent || 0,
                        2,
                      )}
                      %
                    </span>
                  </div>
                  <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[180px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                    <span className="text-sm text-[#2c3b52] leading-tight">
                      Actuals In Conflict
                    </span>
                    <span className="text-base text-[#DC1F00] font-semibold">
                      {formatAccountingStatInt(
                        stats?.shiftActualsConflictCount ??
                          stats?.shift_actuals_conflict_count ??
                          0,
                      )}
                    </span>
                  </div>
                </>
              )}
              {isClosedWeek && (
                <>
                  <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[180px] min-h-[64px] rounded-xl flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm text-[#2c3b52]">Closed</div>
                      {weekClosedLabel && (
                        <div className="text-xs text-[#DC1F00] leading-tight mt-1">
                          {weekClosedLabel}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      title="View Week History"
                      onClick={openAuditLog}
                      className="shrink-0 text-base text-[#DC1F00] font-semibold"
                    >
                      ⋮
                    </button>
                  </div>
                  <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[140px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                    <span className="text-sm text-[#2c3b52] leading-tight">
                      Bill
                    </span>
                    <span className="text-base text-[#DC1F00] font-semibold">
                      {formatAccountingStatMoney(closedWeekBill)}
                    </span>
                  </div>
                  <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[140px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                    <span className="text-sm text-[#2c3b52] leading-tight">
                      Pay
                    </span>
                    <span className="text-base text-[#DC1F00] font-semibold">
                      {formatAccountingStatMoney(totalWeekPay)}
                    </span>
                  </div>
                  <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[180px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                    <span className="text-sm text-[#2c3b52] leading-tight">
                      Margin
                    </span>
                    <span className="text-base text-[#DC1F00] font-semibold">
                      {formatAccountingStatMoney(totalWeekMarginDollar)}
                      {" / "}
                      {formatAccountingStatDecimal(totalWeekMarginPercent, 2)}%
                    </span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Table-Tabs */}
          {selectedTab === "WEEKS" && (
            <div className="flex gap-4 mb-6 border-b border-border overflow-x-auto overscroll-x-contain">
              {["Client Summary", "Payroll Summary"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setInnerTab(tab)}
                  className={`py-2 px-2 text-lg text-[#a15d33] font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                    innerTab === tab
                      ? "border-[#DC1F00] text-red-600 bg-[#DC1F0033] rounded-t-lg"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          )}

          {/* Table for Client Summary */}
          {selectedTab === "WEEKS" && innerTab === "Client Summary" && (
            <div className="atlas-list-table-wrap w-full overflow-x-auto overscroll-x-contain">
              <div className="min-w-[640px]">
              <table className="w-full table-fixed border-collapse border">
              <colgroup>
                <col className="w-[40%]" />
                <col className="w-[20%]" />
                <col className="w-[20%]" />
                <col className="w-[20%]" />
              </colgroup>
              <thead className="bg-[#dc1f0080] text-white">
                <tr className="text-left">
                  <th className="border text-sm font-medium pl-6 py-3 rounded-tl-lg">
                    Client <SortArrow colKey="clientName" />
                  </th>
                  <th className="border text-sm font-medium px-4 py-3 whitespace-nowrap">
                    Bill <SortArrow colKey="bill" />
                  </th>
                  <th className="border text-sm font-medium px-4 py-3 whitespace-nowrap">
                    Pay <SortArrow colKey="pay" />
                  </th>
                  <th className="border text-sm font-medium px-4 py-3 rounded-tr-lg whitespace-nowrap">
                    Margin <SortArrow colKey="margin" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredClientSummary.length === 0 && (
                  <tr className="text-left">
                    <td className="pl-6 py-3" colSpan={4}>
                      No data
                    </td>
                  </tr>
                )}
                {paginatedFilteredClientSummary.map((r) => (
                  <tr
                    key={r.client_id || r.name}
                    className="text-left align-top"
                  >
                    <td className="pl-6 pt-2 pb-1 whitespace-pre-wrap break-words ">
                      <button
                        type="button"
                        onClick={() => {
                          setDetailClient({ id: r.client_id, name: r.name });
                          setShowClientDetailModal(true);
                        }}
                        className="text-[#DC1F00] font-semibold text-left"
                      >
                        {r.name}
                      </button>
                    </td>
                    <td className="pl-4 pt-2 pb-1 whitespace-nowrap">
                      $
                      {Number(r.bill ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="pl-4 pt-2 pb-1 whitespace-nowrap">
                      $
                      {Number(r.pay ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="pl-4 pt-2 pb-1 whitespace-nowrap">
                      {(
                        (((r.bill || 0) - (r.pay || 0)) / (r.bill || 1)) *
                        100
                      ).toFixed(2)}
                      %
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Table for Payroll Summary */}
          {selectedTab === "WEEKS" && innerTab === "Payroll Summary" && (
            <div className="atlas-list-table-wrap w-full overflow-x-auto overscroll-x-contain">
              <div className="min-w-[620px]">
              <table className="w-full table-fixed border-collapse border">
              <colgroup>
                <col className="w-[38%]" />
                <col className="w-[38%]" />
                <col className="w-[24%]" />
              </colgroup>
              <thead className="bg-[#dc1f0080] text-white">
                <tr className="text-left">
                  <th className="border text-sm font-medium pl-6 py-3 rounded-tl-lg">
                    Field Staff <SortArrow colKey="staffName" />
                  </th>
                  <th className="border text-sm font-medium px-4 py-3">
                    Payer <SortArrow colKey="payerName" />
                  </th>
                  <th className="border text-sm font-medium px-4 py-3 rounded-tr-lg whitespace-nowrap">
                    Amount <SortArrow colKey="pay" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredFieldstaffSummary.length === 0 && (
                  <tr className="text-left">
                    <td className="pl-6 py-3" colSpan={3}>
                      No data
                    </td>
                  </tr>
                )}
                {paginatedFilteredFieldstaffSummary.map((r) => (
                  <tr
                    key={r.account_id || r.name}
                    className="text-left align-top"
                  >
                    <td className="pl-6 pt-2 pb-1 whitespace-pre-wrap break-words ">
                      <button
                        type="button"
                        onClick={() => {
                          setDetailPerson({ id: r.account_id, name: r.name });
                          setShowPersonnelDetailModal(true);
                        }}
                        className="text-[#DC1F00] font-semibold text-left"
                      >
                        {r.name}
                      </button>
                    </td>
                    <td className="pl-4 pt-2 pb-1 whitespace-pre-wrap break-words ">
                      {r.payer?.name || ""}
                    </td>
                    <td className="pl-4 pt-2 pb-1 whitespace-nowrap">
                      $
                      {Number(r.pay ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Status card PAY STUBS */}
          {selectedTab === "PAY STUBS" && (
            <div className="flex flex-wrap items-stretch mb-6 gap-3">
              <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[140px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                <span className="text-sm text-[#2c3b52] leading-tight">Staff</span>
                <span className="text-base text-[#DC1F00] font-semibold">
                  {formatAccountingStatInt(paystubDisplayStats?.accounts || 0)}
                </span>
              </div>
              <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[200px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                <span className="text-sm text-[#2c3b52] leading-tight">
                  Shifts totalling Hours
                </span>
                <span className="text-base text-[#DC1F00] font-semibold">
                  {formatAccountingStatInt(paystubDisplayStats?.shifts || 0)} /{" "}
                  {formatAccountingStatDecimal(
                    paystubDisplayStats?.hours || 0,
                    2,
                  )}
                </span>
              </div>
              <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[180px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                <span className="text-sm text-[#2c3b52] leading-tight">
                  Total Pay Before Tax
                </span>
                <span className="text-base text-[#DC1F00] font-semibold">
                  {formatAccountingStatMoney(paystubDisplayStats?.subtotal || 0)}
                </span>
              </div>
              <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[220px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                <span className="text-sm text-[#2c3b52] leading-tight">
                  Actuals Submitted & Not In Conflict
                </span>
                <span className="text-base text-[#DC1F00] font-semibold">
                  {formatAccountingStatDecimal(
                    paystubDisplayStats?.validActualsPercent || 0,
                    2,
                  )}
                  %
                </span>
              </div>
            </div>
          )}

          {/* Status card PAYROLL SUMMARY */}
          {selectedTab === "PAYROLL SUMMARY" && (
            <div className="flex flex-wrap items-stretch mb-6 gap-3">
              <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[140px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                <span className="text-sm text-[#2c3b52] leading-tight">Staff</span>
                <span className="text-base text-[#DC1F00] font-semibold">
                  {formatAccountingStatInt(payroll?.staff || 0)}
                </span>
              </div>
              <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[200px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                <span className="text-sm text-[#2c3b52] leading-tight">
                  Shifts totalling Hours
                </span>
                <span className="text-base text-[#DC1F00] font-semibold">
                  {formatAccountingStatInt(payroll?.shifts || 0)} /{" "}
                  {formatAccountingStatDecimal(payroll?.hours || 0, 2)}
                </span>
              </div>
              <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[180px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                <span className="text-sm text-[#2c3b52] leading-tight">
                  Total Pay Before Tax
                </span>
                <span className="text-base text-[#DC1F00] font-semibold">
                  {formatAccountingStatMoney(payroll?.totalPayBeforeTax || 0)}
                </span>
              </div>
              <div className="border-2 border-[#dc1f0033] p-3 flex-1 min-w-[220px] min-h-[64px] rounded-xl flex flex-col justify-center gap-1">
                <span className="text-sm text-[#2c3b52] leading-tight">
                  Actuals Submitted & Not in Conflict
                </span>
                <span className="text-base text-[#DC1F00] font-semibold">
                  {formatAccountingStatDecimal(
                    payroll?.actualsSubmittedNotInConflict || 0,
                    1,
                  )}
                  %
                </span>
              </div>
            </div>
          )}

          {/* Detailed Payroll Summary cards (top-level) */}
          {selectedTab === "PAYROLL SUMMARY" &&
            (isClosedWeek ? (
              <div className="space-y-6">
                {filteredFieldstaffSummary.length === 0 && (
                  <div className="text-sm text-gray-600">No data</div>
                )}
                {filteredFieldstaffSummary.map((r) => {
                  const lines =
                    Array.isArray(r.lines) && r.lines.length
                      ? r.lines
                      : Array.isArray(r.items) && r.items.length
                        ? r.items
                        : [];
                  return (
                    <div
                      key={r.account_id || r.name}
                      className="bg-white border border-gray-200 rounded-lg p-4"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <StaffAvatarCircle
                            accountId={r.account_id}
                            employee={r}
                            name={r.name}
                            className="w-10 h-10 shrink-0"
                            textClassName="text-sm text-gray-500"
                          />
                          <div className="min-w-0">
                            <div className="font-semibold text-[#DC1F00] break-words">
                              {r.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              Staff ID · {r.account_id || "-"}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm">
                          <div className="flex items-center gap-2">
                            <FaUniversity className="text-gray-600" />
                            <div>
                              <div className="text-gray-600">Payer</div>
                              <div className="font-medium">
                                {r.payer?.name || "—"}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <FaCalendarAlt className="text-gray-600" />
                            <div>
                              <div className="text-gray-600">Pay Period</div>
                              <div className="font-medium">
                                {formatPayPeriodLabel(week?.start_date, week?.end_date)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 border-t pt-3">
                        <div className="atlas-list-table-wrap overflow-x-auto">
                          <div className="min-w-[640px]">
                            <div className="grid grid-cols-12 gap-2 text-xs text-gray-500 font-medium mb-2">
                              <div className="col-span-3">Staff Who Worked</div>
                              <div className="col-span-5">Description</div>
                              <div className="col-span-1 text-right">Quantity</div>
                              <div className="col-span-1 text-right">Rate</div>
                              <div className="col-span-2 text-right">Total</div>
                            </div>

                            {lines.length > 0 ? (
                              lines.map((ln, idx) => (
                                (() => {
                                  const workedByText = String(
                                    ln.workedByName ||
                                      ln.worked_by ||
                                      ln.workedBy ||
                                      "",
                                  ).trim();
                                  const payoutStaffText = String(r.name || "").trim();
                                  const showWorkedBy =
                                    workedByText &&
                                    workedByText.toLowerCase() !==
                                      payoutStaffText.toLowerCase();
                                  return (
                                <div
                                  key={idx}
                                  className="grid grid-cols-12 gap-2 py-2 border-b items-center"
                                >
                                  <div className="col-span-3 text-sm">
                                    {showWorkedBy ? workedByText : ""}
                                  </div>
                                  <div className="col-span-5 text-sm">
                                    {ln.description ||
                                      ln.name ||
                                      ln.role ||
                                      ln.title ||
                                      ""}
                                  </div>
                                  <div className="col-span-1 text-sm text-right">
                                    {ln.quantity ?? ""}
                                  </div>
                                  <div className="col-span-1 text-sm text-right">
                                    {typeof ln.rate !== "undefined"
                                      ? `$${Number(ln.rate).toFixed(2)}`
                                      : ""}
                                  </div>
                                  <div className="col-span-2 text-sm text-right">
                                    {typeof ln.total !== "undefined"
                                      ? `$${Number(ln.total).toFixed(2)}`
                                      : ""}
                                  </div>
                                </div>
                                  );
                                })()
                              ))
                            ) : (
                              // fallback single-line description if available
                              <div className="py-2 text-sm text-gray-600">
                                {r.description || r.job_title || "No line items"}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-3 text-right font-semibold">
                          Total: ${Number(r.total || r.pay || 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-6 bg-yellow-50 border border-yellow-200 rounded text-sm text-gray-700">
                Can't generate payroll summaries for open weeks
              </div>
            ))}

          {/* PAY STUBS grouped report */}
          {selectedTab === "PAY STUBS" && (
            <>
              {!isClosedWeek && (
                <div className="mb-6 p-4 bg-[#F97566] text-white rounded-md text-base">
                  Cannot generate for open weeks.
                </div>
              )}

              {isClosedWeek && filteredPaystubs.length === 0 && (
                <div className="py-12 text-center text-2xl text-gray-400">
                  No Results Found
                </div>
              )}

              {isClosedWeek && filteredPaystubs.length > 0 && (
                <div className="space-y-8">
                  {paginatedFilteredPaystubs.map((group) => {
                    return (
                      <div key={group.account_id} className="border-t border-gray-200 pt-6">
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-start">
                          <div className="col-span-5 flex items-start gap-3 min-w-0">
                            <StaffAvatarCircle
                              accountId={group.account_id}
                              employee={group}
                              name={group.staff_name}
                              className="w-10 h-10 shrink-0"
                              textClassName="text-sm text-gray-500"
                            />
                            <div className="min-w-0">
                              <div className="text-xl text-[#2c3b52] leading-tight break-words">
                                {group.staff_name}
                              </div>
                              {group.staff_address && (
                                <div className="text-sm text-[#5d6b82] leading-snug break-words">
                                  {group.staff_address}
                                </div>
                              )}
                            </div>
                          </div>
                        <div className="col-span-4">
                          <div className="text-lg text-[#2c3b52]">Invoice Submitted to:</div>
                          <div className="text-sm text-[#2c3b52] leading-snug break-words">
                            {group.invoice_submitted_to || group.payer_name || "-"}
                          </div>
                        </div>
                        <div className="col-span-3 text-right">
                          <div className="flex items-start justify-end gap-2">
                            <FaUniversity className="text-gray-700 mt-1" />
                            <div className="text-left">
                              <div className="text-lg text-[#2c3b52]">Pay Period:</div>
                              <div className="text-sm text-[#2c3b52]">
                                {formatPayPeriodLabel(
                                  group.pay_period_start,
                                  group.pay_period_end,
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 atlas-list-table-wrap overflow-x-auto">
                        <div className="min-w-[480px]">
                          <div className="grid grid-cols-[42px_120px_minmax(0,1fr)_90px_90px_100px] gap-2 bg-gray-100 text-sm text-gray-500 px-2 py-2">
                            <div>#</div>
                            <div>Date</div>
                            <div>Client</div>
                            <div className="text-right">Hours</div>
                            <div className="text-right">Rate</div>
                            <div className="text-right">Total</div>
                          </div>

                          <div>
                            {(group.lines || []).map((line) => {
                              const travelHours = accountingLineTravelHours(line);
                              const travelAmount = accountingLineTravelAmount(line);
                              const travelRate = accountingLineTravelRate(line);
                              const shiftTotal = accountingLineShiftTotal(line);
                              const hasTravel = travelAmount > 0;
                              return (
                                <div
                                  key={`${group.account_id}-${line.id}`}
                                  className="border-b border-gray-300"
                                >
                                  <div className={`grid grid-cols-[42px_120px_minmax(0,1fr)_90px_90px_100px] gap-2 px-2 ${hasTravel ? "pt-2 pb-0 leading-tight" : "py-2"} text-base text-[#2c3b52]`}>
                                    <div>{line.row_number}</div>
                                    <div>{formatAppDate(line.date)}</div>
                                    <div className="min-w-0">
                                      <div>{line.client_name || "-"}</div>
                                      <div className="text-sm text-[#5d6b82]">{line.worked_by}</div>
                                    </div>
                                    <div className="text-right">
                                      {Number(line.hours || 0).toFixed(2)}
                                    </div>
                                    <div className="text-right">
                                      ${Number(line.rate || 0).toFixed(2)}
                                    </div>
                                    <div className="text-right">
                                      ${Number(shiftTotal || 0).toFixed(2)}
                                    </div>
                                  </div>
                                  {hasTravel ? (
                                    <div className="grid -mt-1 grid-cols-[42px_120px_minmax(0,1fr)_90px_90px_100px] gap-2 px-2 pt-0 pb-3 leading-tight text-base text-[#2c3b52]">
                                      <div />
                                      <div />
                                      <div />
                                      <div className="text-right">
                                        {Number(travelHours || 0).toFixed(2)}
                                      </div>
                                      <div className="text-right">
                                        ${Number(travelRate || 0).toFixed(2)}
                                      </div>
                                      <div className="text-right">
                                        ${Number(travelAmount || 0).toFixed(2)}
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start justify-between mt-3 text-[#2c3b52] gap-4">
                        <div className="text-sm flex items-center gap-2">
                          {showPaymentEnvelopeIcon(group.payment_note) ? (
                            <FaEnvelope className="text-[#7f6c58]" />
                          ) : null}
                          <span>{group.payment_note || "Payment method not set"}</span>
                        </div>
                        <div className="text-base text-right">
                          <div>
                            Total of {Number(group.total_hours || 0).toFixed(2)} shift hours
                            {Number(group.adjustment_total || 0) !== 0
                              ? " plus adjustments"
                              : ""}{" "}
                            : ${Number(group.footer_subtotal || group.subtotal || 0).toFixed(2)}
                          </div>
                          {group.hst_number ? (
                            <div className="text-sm">
                              HST #{group.hst_number} ({Number(group.hst_percent || 13).toFixed(0)}%): $
                              {Number(group.hst_amount || 0).toFixed(2)}
                            </div>
                          ) : null}
                          <div className="font-medium">
                            Total: $
                            {Number(
                              group.total_with_hst ||
                                group.footer_subtotal ||
                                group.subtotal ||
                                0,
                            ).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Table for Client Reports */}
          {selectedTab === "CLIENT REPORTS" && (
            <div className="atlas-list-table-wrap overflow-x-auto">
              <table className="w-full min-w-[700px] table-fixed border-collapse border">
                <thead className="bg-[#dc1f0080] text-white">
                  <tr className="text-left">
                    <th className="border text-sm font-medium pl-6 py-3 rounded-tl-lg w-2/4">
                      Client <SortArrow colKey="client_name" />
                    </th>
                    <th className="border text-sm font-medium px-4 py-3 w-1/4">
                      Parent Client <SortArrow colKey="parent_name" />
                    </th>
                    <th className="border text-sm font-medium px-4 py-3 rounded-tr-lg w-1/4"></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedFilteredClients.length === 0 && (
                    <tr className="text-left">
                      <td className="pl-6 py-3" colSpan={3}>
                        No data
                      </td>
                    </tr>
                  )}
                  {paginatedFilteredClients.map((c) => (
                    <tr key={c.client_id} className="text-left align-top">
                      <td className="border-y border-gray-300 pl-6 pt-2 pb-1 whitespace-pre-wrap break-words ">
                        <div className="flex items-center gap-3">
                          {c.avatar_url ? (
                            <img
                              src={resolveBackendUrl(c.avatar_url)}
                              alt={c.name || "client"}
                              className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center text-sm flex-shrink-0">
                              {(c.name || "").trim().slice(0, 1) || "C"}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setDetailClient({ id: c.client_id, name: c.name });
                              setShowClientDetailModal(true);
                            }}
                            className="text-[#DC1F00] font-semibold text-left hover:opacity-90"
                          >
                            {c.name}
                          </button>
                        </div>
                      </td>
                      <td className="border-y border-gray-300 pl-4 pt-2 pb-1 whitespace-pre-wrap break-words ">
                        {c.parent_client?.name || ""}
                      </td>
                      <td className="border-y border-gray-300 pl-4 pt-2 pb-1 text-center">
                        <button
                          type="button"
                          onClick={() => {
                            if (!isClosedWeek) {
                              showError("Cannot Generate Report For Open Weeks");
                              return;
                            }
                            exportClientReports("pdf", c.client_id, c.name);
                          }}
                          className={ATLAS_TOOLBAR_BTN_PRIMARY}
                        >
                          DOWNLOAD
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}

          {/* Table for Adjustment */}
          {selectedTab === "ADJUSTMENT" && (
            <div className="atlas-list-table-wrap w-full overflow-x-auto overscroll-x-contain">
              <table className="w-full min-w-[1120px] table-fixed border-collapse border">
              <colgroup>
                <col className="w-[80px]" />
                <col className="w-[190px]" />
                <col className="w-[145px]" />
                <col className="w-[125px]" />
                <col className="w-[70px]" />
                <col className="w-[100px]" />
                <col className="w-[125px]" />
                <col
                  style={{
                    width: adjustmentDescriptionColWidth,
                  }}
                />
                <col className="w-[32px]" />
              </colgroup>
              <thead className="bg-[#dc1f0080] text-white">
                <tr className="text-left">
                  <th className="border text-sm font-medium pl-6 py-3 rounded-tl-lg whitespace-nowrap">
                    ID <SortArrow colKey="adj_id" />
                  </th>
                  <th className="border text-sm font-medium px-3 py-3">
                    Subject
                  </th>
                  <th className="border text-sm font-medium px-3 py-2 whitespace-nowrap">
                    Subject Type
                  </th>
                  <th className="border text-sm font-medium px-3 py-2 whitespace-nowrap">
                    Date <SortArrow colKey="adj_date" />
                  </th>
                  <th className="border text-sm font-medium px-3 py-2 whitespace-nowrap">
                    Qty
                  </th>
                  <th className="border text-sm font-medium px-3 py-2 whitespace-nowrap">
                    Rate
                  </th>
                  <th className="border text-sm font-medium px-4 py-3 whitespace-nowrap">
                    Total
                  </th>
                  <th
                    className="border text-sm font-medium px-4 py-3 whitespace-nowrap"
                  >
                    Line Item Description
                  </th>
                  <th
                    className="border p-0 py-3 rounded-tr-lg"
                    style={{ width: "2rem", minWidth: "2rem", maxWidth: "2rem" }}
                    aria-label="Actions"
                  />
                </tr>
              </thead>
              <tbody>
                {filteredAdjustments.length === 0 && (
                  <tr className="text-left">
                    <td className="pl-6 py-3" colSpan={9}>
                      No data
                    </td>
                  </tr>
                )}
                {filteredAdjustments.map((a) => (
                  <tr key={a.adjustment_id} className="text-left align-top">
                    <td className="px-2 pt-2 pb-1 text-center whitespace-nowrap">
                      <button
                        onClick={() => {
                          setEditAdjustment(a);
                          setShowEditAdjustmentModal(true);
                        }}
                        className="text-red-600 font-semibold hover:opacity-80 cursor-pointer"
                        title={`Edit adjustment ${a.adjustment_id}`}
                      >
                        {a.adjustment_id}
                      </button>
                    </td>
                    <td className="px-3 pt-2 pb-1 whitespace-pre-wrap break-words">
                      <div
                        title={
                          a.subject_name && a.subject_name.trim()
                            ? a.subject_name.trim()
                            : ""
                        }
                      >
                        {a.subject_name && a.subject_name.trim() ? (
                          a.subject_name.trim()
                        ) : (
                          <span className="text-gray-500 italic">
                            {a.account_id
                              ? `Account #${a.account_id}`
                              : a.client_id
                                ? `Client #${a.client_id}`
                                : "Unknown"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 pt-2 pb-1">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          a.subject_type === "Client"
                            ? "bg-blue-100 text-blue-800"
                            : a.subject_type === "Field Staff"
                              ? "bg-green-100 text-green-800"
                              : a.subject_type === "Outsourced Staff"
                                ? "bg-yellow-100 text-yellow-800"
                                : a.subject_type === "Office Staff"
                                  ? "bg-purple-100 text-purple-800"
                                  : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {a.subject_type || "Unknown"}
                      </span>
                    </td>
                    <td className="px-3 pt-2 pb-1 text-center whitespace-nowrap">
                      {formatAppDate(a.recognition_date)}
                    </td>
                    <td className="px-3 pt-2 pb-1 text-center whitespace-nowrap">
                      {Number(a.quantity) || 0}
                    </td>
                    <td className="px-3 pt-2 pb-1 text-right whitespace-nowrap">
                      {"$" + (Number(a.amount) || 0).toFixed(2)}
                    </td>
                    <td className="px-3 pt-2 pb-1 text-right whitespace-nowrap">
                      <div>
                        {"$" + (Number(a.total) || 0).toFixed(2)}
                        {a.add_hst && (
                          <span className="text-xs text-gray-500 ml-1">
                            (incl. HST)
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      className="px-3 pt-2 pb-1 align-top whitespace-pre-wrap break-words"
                    >
                      <div title={a.description || "No description"}>
                        {a.description || (
                          <span className="text-gray-400 italic">
                            No description
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      className="p-0 pt-2 pb-1 text-center"
                      style={{ width: "2rem", minWidth: "2rem", maxWidth: "2rem" }}
                    >
                      {String(a.week_status || "").toLowerCase() === "open" ? (
                        <ActionDropdown
                          actions={
                            isDeletedState(a.state)
                              ? [
                                  {
                                    label: "Restore",
                                    onClick: async () => {
                                      if (
                                        !(await showConfirm(
                                          `Are you sure you want to restore adjustment #${a.adjustment_id}?`,
                                        ))
                                      ) {
                                        return;
                                      }
                                      await restoreAdjustment(a);
                                    },
                                  },
                                ]
                              : [
                                  {
                                    label: "Delete",
                                    onClick: async () => {
                                      if (
                                        !(await showConfirm(
                                          `Are you sure you want to delete adjustment #${a.adjustment_id}?`,
                                        ))
                                      ) {
                                        return;
                                      }
                                      await deleteAdjustment(a);
                                    },
                                  },
                                ]
                          }
                        />
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          )}

          {/* Table for Payers */}
          {selectedTab === "PAYERS" && (
            <div className="atlas-list-table-wrap overflow-x-auto">
              <table className="w-full min-w-[800px] table-fixed border-collapse border">
              <thead className="bg-[#dc1f0080] text-white">
                <tr className="text-left">
                  <th className="border text-sm font-medium pl-6 py-3 rounded-tl-lg w-1/4">
                    Name <SortArrow colKey="name" />
                  </th>
                  <th className="border text-sm font-medium px-3 py-3 w-1/4">
                    Worker Type <SortArrow colKey="worker_type" />
                  </th>
                  <th className="border text-sm font-medium px-3 py-2">
                    Legal Information
                  </th>
                  <th
                    className="border p-0 py-3 rounded-tr-lg"
                    style={{ width: "2rem", minWidth: "2rem", maxWidth: "2rem" }}
                    aria-label="Actions"
                  />
                </tr>
              </thead>
              <tbody>
                {filteredPayers.length === 0 && (
                  <tr className="text-left">
                    <td className="pl-6 py-3" colSpan={4}>
                      No data
                    </td>
                  </tr>
                )}
                {paginatedFilteredPayers.map((p) => (
                  <tr key={p.payer_id} className="text-left align-top">
                    <td className="px-2 pt-2 pb-1 whitespace-pre-wrap break-words ">
                      <button
                        type="button"
                        onClick={() => openEditPayer(p)}
                        className="text-[#DC1F00] font-semibold hover:opacity-90 cursor-pointer"
                        style={{ textDecoration: "none" }}
                      >
                        {p.name}
                      </button>
                    </td>
                    <td className="pl-4 pt-2 pb-1 whitespace-pre-wrap break-words ">
                      {p.worker_type}
                    </td>
                    <td className="pl-4 pt-2 pb-1 whitespace-pre-wrap break-words ">
                      {p.legal_name}
                    </td>
                    <td
                      className="p-0 pt-2 pb-1 text-center"
                      style={{ width: "2rem", minWidth: "2rem", maxWidth: "2rem" }}
                    >
                      <ActionDropdown
                        actions={
                          isDeletedState(p.state)
                            ? [
                                {
                                  label: "Restore",
                                  onClick: async () => {
                                    if (
                                      !(await showConfirm(
                                        `Are you sure you want to restore ${p.name}?`,
                                      ))
                                    )
                                      return;
                                    restorePayer(p.payer_id);
                                  },
                                },
                              ]
                            : [
                                {
                                  label: "Delete",
                                  onClick: async () => {
                                    if (
                                      !(await showConfirm(
                                        `Are you sure you want to delete ${p.name}?`,
                                      ))
                                    )
                                      return;
                                    deletePayer(p.payer_id);
                                  },
                                },
                              ]
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-600">
              {(selectedTab === "ADJUSTMENT" ||
                selectedTab === "CLIENT REPORTS" ||
                selectedTab === "WEEKS" ||
                selectedTab === "PAYROLL SUMMARY" ||
                selectedTab === "PAY STUBS" ||
                selectedTab === "PAYERS") &&
              !(selectedTab === "PAYROLL SUMMARY" && !isClosedWeek)
                ? (() => {
                    const total = effectiveListTotal;
                    if (total === 0) return `Showing 0 of 0 results`;
                    const start = (currentPage - 1) * rowsPerPage + 1;
                    const end = Math.min(currentPage * rowsPerPage, total);
                    return `Showing ${start} - ${end} of ${total} results`;
                  })()
                : null}
            </div>
            <nav
              className="flex items-center mt-auto text-sm text-muted-foreground gap-2"
              aria-label="Pagination"
            >
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 border border-[#DC1F0033] text-buttonWhite rounded-md hover:bg-gray-100 disabled:opacity-50"
              >
                Previous
              </button>

              {getPageList(effectiveListTotalPages, currentPage).map((p, idx) =>
                typeof p === "string" ? (
                  <span key={`dots-${idx}`} className="px-2.5 py-1.5">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    aria-current={p === currentPage ? "page" : undefined}
                    className={`px-2.5 py-1.5 border border-[#DC1F0033] rounded-md ${p === currentPage ? "bg-[#DC1F00] text-white" : "hover:bg-gray-100"}`}
                  >
                    {p}
                  </button>
                ),
              )}

              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, effectiveListTotalPages))
                }
                disabled={currentPage === effectiveListTotalPages}
                className="px-2.5 py-1.5 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </nav>
          </div>
            </div> {/* closing contentOffsetClass wrapper */}
        </fieldset>

        <AtlasFilterPanel
          isOpen={filterPanel.isOpen}
          onClose={filterPanel.close}
          onClear={clearAllFilters}
          onClearAll={clearAllFilters}
        >
          {renderAccountingFilterFields()}
        </AtlasFilterPanel>
        </div> {/* closing bordered tab container */}
      </div> {/* closing flex-col min-h-screen */}

      {showAudit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="font-semibold">
                Audits for week of{" "}
                {formatPayPeriodLabel(week?.start_date, week?.end_date) || "-"}
              </div>
              <button
                onClick={() => setShowAudit(false)}
                className="text-sm px-2 py-1 border rounded"
              >
                Close
              </button>
            </div>
            <div className="p-4 overflow-auto" style={{ maxHeight: "78vh" }}>
              <RichAuditsPanel
                subjectId={week?.week_id}
                fetchUrl={
                  week?.week_id
                    ? `${BASE_URL}/api/accounting/audits/rich?weekId=${encodeURIComponent(week.week_id)}`
                    : ""
                }
                title={`Audits for week of ${formatPayPeriodLabel(week?.start_date, week?.end_date) || "-"}`}
              />
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};



export default Accounting;

